// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';
import TextField from './components/TextField';
import Autocomplete, { type AutocompleteRenderInputParams } from './components/Autocomplete';
import Snackbar from './components/Snackbar';
import { ClearIcon, EditIcon, FitToViewIcon } from './components/icons';
import IconButton from './components/IconButton';
import SpeedDial, { CloseReason, SpeedDialAction, SpeedDialIcon } from './components/SpeedDial';
import Button from './components/Button';
import { canonicalize } from '@simlin/core/canonicalize';

import {
  LinkPolarity,
  LoopPolarity,
  Model as EngineModel,
  Project as EngineProject,
  SimlinErrorKind,
  SimlinUnitErrorKind,
} from '@simlin/engine';
import type {
  JsonProjectPatch,
  JsonModelOperation,
  JsonSimSpecs,
  JsonArrayedEquation,
  JsonProject,
  JsonModel,
} from '@simlin/engine';
import type { ErrorDetail } from '@simlin/engine';
import { stockFlowViewToJson } from './view-conversion';
import {
  Project,
  Model,
  Variable,
  UID,
  Aux,
  ViewElement,
  NamedViewElement,
  StockFlowView,
  GraphicalFunction,
  LinkViewElement,
  FlowViewElement,
  StockViewElement,
  CloudViewElement,
  viewElementType,
  EquationError,
  SimError,
  ModelError,
  ErrorCode,
  Rect,
  UnitError,
  projectFromJson,
  projectAttachData,
  isNamedViewElement,
  stockToJson,
  flowToJson,
  auxToJson,
  moduleToJson,
  type ModuleReference,
} from '@simlin/core/datamodel';
import { defined, exists, mapSet, Series, setsEqual, toInt, uint8ArraysEqual } from '@simlin/core/common';
import { first, getOrThrow, last, only } from '@simlin/core/collections';

import { AuxIcon } from './AuxIcon';
import { Toast } from './ErrorToast';
import { FlowIcon } from './FlowIcon';
import { LinkIcon } from './LinkIcon';
import { ModuleIcon } from './ModuleIcon';
import { ModelPropertiesDrawer } from './ModelPropertiesDrawer';
import { renderSvgToString } from './render-common';
import { CLDArrowPolarity, cldArrowKey } from './drawing/cld/CLDArrow';
import { CLDLoopPolarity } from './drawing/cld/CLDLoopLabel';
import { DEFAULT_RENDER_MODE, RenderMode } from './render-mode';
import { RenderModeToggle } from './RenderModeToggle';
import { Status } from './Status';
import { SaveControl } from './SaveControl';
import { SaveIdle, SaveSaved, SaveSaving, SaveUnsaved, ReasonUnknown, reasonForError, SaveError } from './save-status';
import type { SaveStatus } from './save-status';
import { StockIcon } from './StockIcon';
import { UndoRedoBar } from './UndoRedoBar';
import { VariableDetails } from './VariableDetails';
import { ActivityRail, ActivityTab, DEFAULT_ACTIVITY_TABS, ActivityRailTab } from './ActivityRail';
import { AgentPanel } from './AgentPanel';
import { availablePersonas, WS4_PERSONAS, AgentPersona, canApplyInPlace, PERSONAS } from './agent-personas';
import {
  AgentSession,
  initialSession,
  selectPersona,
  startSend,
  applyResult,
  resolveAndMaybeApply,
  cancel as cancelAgentSession,
  decide as decideAgentSession,
} from './agent-session';
import { findTurn, ProposeFn, DescribeFn, AgentResult, ProposalSummary, conversationContext } from './agent-conversation';
import { Tier } from '@simlin/core/tier';
import { PlayPanel } from './PlayPanel';
import { modelParameters } from './play-parameters';
import { ResultsBand } from './ResultsBand';
import { ZoneFrame } from './ZoneFrame';
import { MIN_ACTIVITIES_WIDTH, MIN_RESULTS_HEIGHT } from './zone-frame';
import { ringLayout } from './ring-layout';
import { autoLayout } from './auto-layout';
import { buildCldView, spreadOverlaps, CLD_MIN_NODE_SPACING } from './cld-view';
import { BandLayout, DEFAULT_BAND_LAYOUT } from './results-band-layout';
import { LoopPicker } from './LoopPicker';
import { rankLoops } from './loop-ranking';
import { loopHighlights } from './loop-highlight';
import { BuildLoopsPlaySwitch, ViewMode } from './BuildLoopsPlaySwitch';
import { DensitySelector } from './DensitySelector';
import { PaperSelector, CanvasPaper } from './PaperSelector';
import { DensityPreset, DEFAULT_DENSITY, densityConfig } from './zone-density';
import { assignLoopColors, paperKind } from './canvas-notation';
import { ModuleDetails } from './ModuleDetails';
import { ErrorDetails } from './ErrorDetails';
import { ZoomBar } from './ZoomBar';
import { Canvas, fauxCloudTargetUid, inCreationCloudUid, inCreationUid } from './drawing/Canvas';
import { Point, searchableName } from './drawing/common';
import { UpdateCloudAndFlow } from './drawing/Flow';
import { applyGroupMovement } from './group-movement';
import { detectUndoRedo, isEditableElement } from './keyboard-shortcuts';
import { preserveLiveView } from './merge-live-view';
import { type ModuleStackEntry, currentModelName, pushModule, popModule, navigateToLevel, isStdlibModel } from './module-navigation';
import { countModelInstances } from './module-details-utils';
import { BreadcrumbBar } from './BreadcrumbBar';

import styles from './Editor.module.css';

const MaxUndoSize = 5;
// These must stay in sync with --panel-width-sm and --panel-width-lg in theme.css
const SearchbarWidthSm = 359;
const SearchbarWidthLg = 480;

function convertErrorDetails(
  errors: ErrorDetail[],
  modelName: string,
): {
  varErrors: ReadonlyMap<string, readonly EquationError[]>;
  unitErrors: ReadonlyMap<string, readonly UnitError[]>;
} {
  const varErrors = new Map<string, EquationError[]>();
  const unitErrors = new Map<string, UnitError[]>();

  for (const err of errors) {
    if (err.modelName !== modelName) {
      continue;
    }

    const ident = err.variableName;
    if (!ident) {
      continue;
    }

    const isUnitError = err.kind === SimlinErrorKind.Units;

    if (isUnitError) {
      const unitError: UnitError = {
        start: err.startOffset ?? 0,
        end: err.endOffset ?? 0,
        code: err.code as unknown as ErrorCode,
        isConsistencyError: err.unitErrorKind === SimlinUnitErrorKind.Consistency,
        details: err.message ?? undefined,
      };
      let existing = unitErrors.get(ident);
      if (!existing) {
        existing = [];
        unitErrors.set(ident, existing);
      }
      existing.push(unitError);
    } else {
      const eqError: EquationError = {
        start: err.startOffset ?? 0,
        end: err.endOffset ?? 0,
        code: err.code as unknown as ErrorCode,
      };
      let existing = varErrors.get(ident);
      if (!existing) {
        existing = [];
        varErrors.set(ident, existing);
      }
      existing.push(eqError);
    }
  }

  return { varErrors, unitErrors };
}

class EditorError implements Error {
  name = 'EditorError';
  message: string;
  constructor(msg: string) {
    this.message = msg;
  }
}

interface ErrorDetailsLike {
  code?: unknown;
  message?: string;
  details?: unknown;
}

function getErrorDetails(error: unknown): ErrorDetailsLike {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as Record<string, unknown>;
    return {
      code: maybeError.code,
      message: typeof maybeError.message === 'string' ? maybeError.message : undefined,
      details: maybeError.details,
    };
  }
  if (typeof error === 'string') {
    return { message: error };
  }
  return {};
}

interface CachedErrorDetails {
  varErrors: ReadonlyMap<string, readonly EquationError[]>;
  unitErrors: ReadonlyMap<string, readonly UnitError[]>;
  simError: SimError | undefined;
  modelErrors: readonly ModelError[];
}

interface EditorState {
  modelErrors: readonly Error[];
  activeProject: Project | undefined;
  projectHistory: readonly Readonly<Uint8Array>[];
  projectOffset: number;
  modelName: string;
  modelStack: ReadonlyArray<ModuleStackEntry>;
  dialOpen: boolean;
  dialVisible: boolean;
  selectedTool: 'stock' | 'flow' | 'aux' | 'link' | 'module' | undefined;
  data: ReadonlyMap<string, Series>;
  selection: ReadonlySet<UID>;
  status: 'ok' | 'error' | 'disabled';
  showDetails: 'variable' | 'errors' | undefined;
  flowStillBeingCreated: boolean;
  drawerOpen: boolean;
  projectVersion: number;
  snapshotBlob: Blob | undefined;
  variableDetailsActiveTab: number;
  // Active tab of the shared right rail (Activities zone, WS2). Variable is the
  // inherited editor; Play is new; Signal/Agents are disabled placeholders.
  activeActivityTab: ActivityTab;
  // Build / Loops / Play view (WS2). Build/Play render the SFD diagram; Loops
  // renders CLD (interactive loops). Play also makes the rail's Play tab
  // reachable without a variable selected.
  viewMode: ViewMode;
  // Play (WS2): per-parameter override values (ident -> value), and whether a
  // Play re-simulation is in flight (drives the PlayPanel button state).
  playOverrides: ReadonlyMap<string, number>;
  playSimulating: boolean;
  // Interactive loop highlight (WS2, AC2.2): the loop currently hovered
  // (transient) and the one pinned by a click. The active loop's member
  // variables are emphasised on the canvas via the variable-highlight overlay.
  hoveredLoopId: string | undefined;
  // CO-5: several loops can be pinned at once (persistent multi-highlight).
  pinnedLoopIds: ReadonlySet<string>;
  // Results band size/mode (WS2, AC2.3): drag-resizable inline height plus a
  // maximized (pop-out) overlay. Owned here so it survives re-renders.
  resultsBandLayout: BandLayout;
  // WS8a zone frame: drag-resizable widths/heights for the Activities rail and
  // the Results band. The frame clamps them (computeZoneLayout) so the Model is
  // never starved; these are the raw requested sizes.
  zoneActivitiesWidth: number;
  zoneResultsHeight: number;
  // WS8a density preset (Guide/Support/Focus). Sets the sliver height and the
  // base Activities/Results sizes above; drag-resize fine-tunes from there.
  density: DensityPreset;
  // WS8a canvas paper (cream/white/black). Live here so the sliver paper picker
  // can switch it -- the locked notation inverts on black (D5). Initialized from
  // the host's canvasPaper prop. The full Settings surface is CO-3 (W8); this is
  // the notation-critical paper choice only.
  canvasPaper: CanvasPaper;
  cachedErrors: CachedErrorDetails;
  // D2 criterion 14: active diagram render mode (SFD vs CLD).  Per
  // criterion 20 the same model data backs both modes; toggling does
  // not mutate the project, so this lives in Editor state rather than
  // in `activeProject`.
  renderMode: RenderMode;
  // D2 criterion 16: link polarities keyed by `cldArrowKey(from, to)`
  // and refreshed whenever the engine project changes.  Empty until
  // the first async fetch completes; CLDArrow renders `'unknown'` (a
  // gray '?') for any link missing a polarity entry so the diagram
  // is never blank during the load.
  linkPolarities: ReadonlyMap<string, CLDArrowPolarity>;
  // D2 criterion 17: feedback loops detected by the engine, refreshed
  // on the same trigger as `linkPolarities`.  Each entry carries the
  // ordered variable idents around the loop plus a polarity so
  // Canvas can compute centroids and render R/B labels.  Order
  // determines per-polarity numbering ("R1", "R2", "B1", ...) so we
  // preserve whatever order `Model.getLoops()` returns.
  loops: ReadonlyArray<{ id: string; polarity: CLDLoopPolarity; variables: readonly string[] }>;
  // CO-4 completion (Williams 2026-08-07): per-loop LTM dominance share, keyed by
  // loop id and normalized to sum ~1, from the most recent simulation. Drives the
  // Loop Picker's ranking + percentages and the per-loop color-by-rank. Empty when
  // no LTM analysis has run (or it failed on a dense graph) -> loops keep engine
  // order with no percentages.
  loopScores: ReadonlyMap<string, number>;
  // CO-5 #9: which variables the Results band plots. `undefined` = the default
  // (the model's stocks); once the user adds/removes a variable it becomes an
  // explicit set they control.
  resultsSeriesIdents: ReadonlySet<string> | undefined;
  // WS4 Phase 1c-iii: the agent session (shared conversation + active persona +
  // busy + pending proposals). The Editor is the imperative shell over the pure
  // agent-session core; the Agents tab renders this and emits intent back.
  agentSession: AgentSession;
  // Outcome of the latest save, shown beside the Save button. Set to 'saved'
  // only when onSave resolves with a server version (see save()).
  saveStatus: SaveStatus;
}

// Discriminated union types for project data formats
export type ProtobufProjectData = {
  format: 'protobuf';
  data: Readonly<Uint8Array>;
};

export type JsonProjectData = {
  format: 'json';
  data: string;
};

export type ProjectData = ProtobufProjectData | JsonProjectData;

type ProtobufInputProps = {
  inputFormat: 'protobuf';
  initialProjectBinary: Readonly<Uint8Array>;
  onSave: (project: ProtobufProjectData, currVersion: number) => Promise<number | undefined>;
};

type JsonInputProps = {
  inputFormat: 'json';
  initialProjectJson: string;
  onSave: (project: JsonProjectData, currVersion: number) => Promise<number | undefined>;
};

type ProjectInputProps = ProtobufInputProps | JsonInputProps;

interface EditorPropsBase {
  initialProjectVersion: number;
  name: string; // used when saving
  embedded?: boolean;
  readOnlyMode?: boolean;
  // False when the host does not store the model anywhere (the backend-free
  // demo). onSave is still called and its returned version still advances the
  // Editor's version bookkeeping, but it is never shown as a save: the toolbar
  // shows "Demo -- changes aren't saved" and no save status. Default true.
  persists?: boolean;
  // Optional selection callback fired after each selection change. Hosts
  // (e.g. simlin-serve's EditorHost) use this to forward selection state
  // to backend listeners; HostedWebEditor in src/app does not subscribe.
  onSelectionChanged?: (idents: string[]) => void;
  // Optional dashboard-navigation callback. Forwarded to BreadcrumbBar
  // so the Home toolbar icon appears only when the host provides a
  // route to navigate to. Keeps Editor decoupled from any specific
  // router (wouter, react-router, none).
  onHome?: () => void;
  // Optional import callback (hamburger > Open model). The host reads the file,
  // opens it in the engine, and re-mounts the Editor with the new project. Absent
  // -> the Open control is hidden (e.g. hosts without a re-open path).
  onOpenModel?: (data: Uint8Array, filename: string) => void;
  // CO-3 local-first editor preferences, supplied by the host from the Settings
  // dialog. The Editor stays decoupled from the app's storage: these arrive as
  // plain props. Omitted -> the built-in defaults (Build view, grid on, cream).
  initialViewMode?: ViewMode;
  showCanvasGrid?: boolean;
  canvasPaper?: CanvasPaper;
  /** WS4: inject the Merlin proposer (app -> server, demo -> a dev stub). Absent -> Agents tab disabled. */
  propose?: ProposeFn;
  /** WS4: inject the Athena describer (read-only model description). Absent -> Athena is unavailable. */
  describe?: DescribeFn;
  /** The user's access tier, for gating which agent personas are reachable (F1). Default Public. */
  agentTier?: Tier;
}

export type EditorProps = EditorPropsBase & ProjectInputProps;

export class Editor extends React.PureComponent<EditorProps, EditorState> {
  engineProject?: EngineProject;
  newEngineShouldPullView = false;
  newEngineQueuedView?: StockFlowView;

  inSave = false;
  saveQueued = false;
  // Set when the view (pan/zoom) changes while a save is in flight: that save
  // serialized the engine before the change, so its success must not read as
  // "Saved". Cleared when a save starts.
  viewChangedDuringSave = false;
  // Pending setTimeout(0) handle for the deferred onSelectionChanged
  // callback. Held so componentWillUnmount can cancel — without that,
  // a remount triggered by an EditorHost path swap (which keys the
  // Editor by `${path}#${loadGeneration}`) would let the prior
  // instance's callback fire against the new instance, re-introducing
  // the stale-idents-on-new-path bug.
  private selectionDeferralTimer: ReturnType<typeof setTimeout> | null = null;
  // Tracks mount state so async chains kicked off by the editor
  // (simulation runs, error refreshes, project loads) can skip their
  // setState calls when the component has unmounted mid-flight.  Set
  // true in componentDidMount, false in componentWillUnmount.
  private _isMounted = false;

  // WS4: monotonic counter for agent turn ids. Ids are generated in the shell
  // (kept out of the pure agent-session core) so its transitions stay
  // deterministic and testable; the counter just needs to be unique per Editor.
  private agentIdSeq = 0;

  private nextAgentId(kind: string): string {
    this.agentIdSeq += 1;
    return `agent-${kind}-${this.agentIdSeq}`;
  }

  constructor(props: EditorProps) {
    super(props);

    // CO-3: open in the host's preferred view (Loops -> CLD, Build/Play -> SFD),
    // falling back to the built-in Build/SFD default.
    const initialViewMode: ViewMode = props.initialViewMode ?? 'build';
    const initialRenderMode: RenderMode = initialViewMode === 'loops' ? 'cld' : DEFAULT_RENDER_MODE;

    this.state = {
      activeProject: undefined,
      projectHistory:
        props.inputFormat === 'protobuf'
          ? [props.initialProjectBinary]
          : [],
      projectOffset: 0,
      modelErrors: [],
      modelName: 'main',
      modelStack: [],
      dialOpen: false,
      dialVisible: true,
      selectedTool: undefined,
      data: new Map<string, Series>(),
      selection: new Set<number>(),
      status: 'disabled',
      showDetails: undefined,
      flowStillBeingCreated: false,
      drawerOpen: false,
      projectVersion: props.initialProjectVersion,
      snapshotBlob: undefined,
      variableDetailsActiveTab: 0,
      activeActivityTab: 'variable',
      viewMode: initialViewMode,
      playOverrides: new Map<string, number>(),
      playSimulating: false,
      hoveredLoopId: undefined,
      pinnedLoopIds: new Set<string>(),
      resultsBandLayout: DEFAULT_BAND_LAYOUT,
      zoneActivitiesWidth: densityConfig(DEFAULT_DENSITY).activitiesWidth,
      zoneResultsHeight: densityConfig(DEFAULT_DENSITY).resultsHeight,
      density: DEFAULT_DENSITY,
      canvasPaper: props.canvasPaper ?? 'white',
      cachedErrors: {
        varErrors: new Map<string, readonly EquationError[]>(),
        unitErrors: new Map<string, readonly UnitError[]>(),
        simError: undefined,
        modelErrors: [],
      },
      renderMode: initialRenderMode,
      linkPolarities: new Map<string, CLDArrowPolarity>(),
      loops: [],
      loopScores: new Map<string, number>(),
      resultsSeriesIdents: undefined,
      agentSession: initialSession('merlin'),
      saveStatus: SaveIdle,
    };
  }

  componentDidMount() {
    this._isMounted = true;

    // Read-only viewers are told so by the toolbar's SaveControl ("View only"),
    // which reads the live readOnlyMode prop. This used to be a one-shot toast
    // pushed here at mount; because the hosted app can mount the Editor before
    // the session finishes loading (readOnlyMode briefly true for the owner),
    // that toast could stay up while saves were in fact succeeding.

    document.addEventListener('keydown', this.handleKeyDown);

    // Initial project load + simulation run, previously kicked off from
    // the constructor via setTimeout.  Moving this into componentDidMount
    // ensures the engine's async chain (which calls refreshCachedErrors
    // and then setState on the cached errors) only runs after React has
    // committed the mount, so the setState always lands on a mounted
    // component.
    void (async () => {
      await this.openInitialProject();
      this.scheduleSimRun();
    })();
  }

  componentWillUnmount() {
    this._isMounted = false;
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this.selectionDeferralTimer !== null) {
      clearTimeout(this.selectionDeferralTimer);
      this.selectionDeferralTimer = null;
    }
  }

  componentDidUpdate(prevProps: EditorProps, prevState: EditorState) {
    // D2 criterion 16: keep `linkPolarities` in sync with the engine's
    // most recent analysis.  The engine recomputes link polarities as a
    // side effect of every patch, so we refetch whenever the optimistic
    // `projectVersion` advances or the user navigates to a different
    // sub-model.  CLD mode-switch alone does not invalidate the cache.
    if (
      prevState.projectVersion !== this.state.projectVersion ||
      prevState.modelName !== this.state.modelName
    ) {
      void this.refetchLinkPolarities();
    }
  }

  /**
   * Fetch per-link polarities + detected feedback loops for the
   * active model and stash them in state.  Both are surfaced as
   * engine analysis side-effects of the same compile, so we run them
   * in parallel and update state once for both.  Errors are
   * swallowed -- a missing polarity / empty loop list still produces
   * a valid (if incomplete) CLD render, which is strictly better
   * than crashing the editor when the engine is between two
   * incremental compiles.
   */
  async refetchLinkPolarities(): Promise<void> {
    const project = this.engineProject;
    if (!project) {
      return;
    }
    try {
      const model = await project.getModel(this.state.modelName);
      const [links, loops] = await Promise.all([model.getLinks(), model.loops()]);

      const nextLinks = new Map<string, CLDArrowPolarity>();
      for (const link of links) {
        let polarity: CLDArrowPolarity = 'unknown';
        if (link.polarity === LinkPolarity.Positive) {
          polarity = 'positive';
        } else if (link.polarity === LinkPolarity.Negative) {
          polarity = 'negative';
        }
        nextLinks.set(cldArrowKey(link.from, link.to), polarity);
      }

      const nextLoops = loops.map((loop) => {
        let polarity: CLDLoopPolarity = 'unknown';
        if (loop.polarity === LoopPolarity.Reinforcing) {
          polarity = 'reinforcing';
        } else if (loop.polarity === LoopPolarity.Balancing) {
          polarity = 'balancing';
        }
        return { id: loop.id, polarity, variables: loop.variables };
      });

      this.setState({ linkPolarities: nextLinks, loops: nextLoops }, () => {
        // If the user is already in Loops when detection (re)completes, make sure
        // the CLD view exists (e.g. loops weren't ready at mode-switch time).
        if (this.state.viewMode === 'loops') {
          void this.ensureCldView();
        }
      });
    } catch {
      // Engine not ready, model missing, or analysis failed; leave
      // the existing state in place so we don't flicker arrows or
      // loop labels off briefly during a transient error.
    }
  }

  handleKeyDown = (e: KeyboardEvent) => {
    // Don't handle shortcuts in embedded mode or editable fields
    if (this.props.embedded || isEditableElement(e.target)) {
      return;
    }

    // Delete / Backspace removes the selected canvas element(s) -- stocks, flows,
    // auxes, links, modules. handleSelectionDelete already rewires a deleted
    // stock's flows to clouds, so this covers stocks + flows, not just the
    // variable panel's delete button (WS8a punch-list #4: delete on the canvas).
    if (
      (e.key === 'Delete' || e.key === 'Backspace') &&
      this.state.selection.size > 0 &&
      !isStdlibModel(this.state.modelName)
    ) {
      e.preventDefault();
      void this.handleSelectionDelete();
      return;
    }

    const action = detectUndoRedo(e);
    if (!action) {
      return;
    }

    const isEnabled = action === 'undo' ? this.isUndoEnabled() : this.isRedoEnabled();
    if (isEnabled) {
      e.preventDefault();
      this.handleUndoRedo(action);
    }
  };

  private isUndoEnabled(): boolean {
    return this.state.projectHistory.length > 1 && this.state.projectOffset < this.state.projectHistory.length - 1;
  }

  private isRedoEnabled(): boolean {
    return this.state.projectOffset > 0;
  }

  project(): Project | undefined {
    return this.state.activeProject;
  }

  engine(): EngineProject | undefined {
    return this.engineProject;
  }

  scheduleSimRun(): void {
    setTimeout(() => {
      const engine = this.engine();
      if (!engine) {
        return;
      }
      this.loadSim(engine);
    });
  }

  async loadSim(engine: EngineProject, overrides: Record<string, number> = {}) {
    await this.recalculateStatus();

    if (!(await engine.isSimulatable())) {
      return;
    }
    // Sparklines don't need Loops-That-Matter analysis, and LTM compilation
    // can blow up WASM memory on dense causal graphs (World3: ~1.8M
    // elementary circuits → `RuntimeError: unreachable` from the allocator).
    // We ask for a plain simulation first; on any failure we retry with LTM
    // explicitly disabled so a future default flip cannot starve the UI of
    // sparkline data.  The first failure is surfaced as a warning-style
    // entry in `modelErrors` so the error panel still shows the user what
    // went wrong.
    const model = await engine.mainModel();
    let run;
    try {
      run = await model.run(overrides);
    } catch (e) {
      this.setState({
        modelErrors: [...this.state.modelErrors, e as Error],
      });
      try {
        run = await model.run(overrides, { analyzeLtm: false });
      } catch (e2) {
        this.setState({
          modelErrors: [...this.state.modelErrors, e2 as Error],
        });
        await this.refreshCachedErrors();
        return;
      }
    }
    const idents = run.varNames;
    const time = run.getSeries('time') ?? new Float64Array(0);
    const data = new Map<string, Series>(
      idents.map((ident) => {
        const values = run.getSeries(ident) ?? new Float64Array(0);
        return [ident, { name: ident, time, values }];
      }),
    );
    const project = defined(this.project());
    // Simulation data comes from mainModel(), so variable idents are
    // root-model-scoped. Always attach data to 'main' so root sparklines
    // stay populated even when a sim runs while viewing a child model.
    this.setState({
      activeProject: projectAttachData(project, data, 'main'),
      data,
    });
    // Refresh cached errors after simulation so the error panel reflects
    // any new simulation errors (e.g. runtime divide-by-zero).
    await this.refreshCachedErrors();

    // CO-4 completion: fetch per-loop LTM dominance shares so the Loop Picker can
    // rank + show percentages and color by rank. Uses the same overrides so the
    // ranking tracks the levers the user just played with (re-ranks on re-sim).
    const loopScores = await this.fetchLoopDominance(model, overrides);
    this.setState({ loopScores });
  }

  /**
   * Run an LTM analysis and reduce each loop's relative-dominance-over-time to a
   * single normalized share (time-averaged |score|, shares summing to ~1). LTM is
   * guarded: it can exhaust WASM memory on dense causal graphs, so any failure
   * yields an empty map and the picker simply falls back to engine order with no
   * percentages. The scratch sim is disposed to free its WASM handle.
   */
  private async fetchLoopDominance(
    model: EngineModel,
    overrides: Record<string, number>,
  ): Promise<ReadonlyMap<string, number>> {
    const shares = new Map<string, number>();
    try {
      const loops = await model.loops();
      if (loops.length === 0) {
        return shares;
      }
      const sim = await model.simulate(overrides, { enableLtm: true });
      try {
        await sim.runToEnd();
        const raw = new Map<string, number>();
        let total = 0;
        for (const loop of loops) {
          const series = await sim.getRelativeLoopScore(loop.id);
          let sum = 0;
          for (const v of series) {
            sum += Math.abs(v);
          }
          const avg = series.length > 0 ? sum / series.length : 0;
          raw.set(loop.id, avg);
          total += avg;
        }
        if (total > 0) {
          for (const [id, v] of raw) {
            shares.set(id, v / total);
          }
        }
      } finally {
        await sim.dispose();
      }
    } catch {
      // LTM unavailable / too dense: leave shares empty (unranked, no %).
      return new Map<string, number>();
    }
    return shares;
  }

  // `pushHistory` controls the undo stack: a real model edit (the default) prepends
  // a new undo step, while a view-only update (pan/zoom/layout via queueViewUpdate,
  // pushHistory=false) REPLACES the current entry -- capturing the latest view
  // without creating an undo step, and, crucially, without evicting a real edit out
  // of the bounded (MaxUndoSize) history. Before this split, every optimistic view
  // update pushed a snapshot, so a burst of view churn after an edit (e.g. the
  // auto-layout that follows an agent-applied change) could evict the pre-edit state
  // and make undo restore a post-edit snapshot.
  async updateProject(serializedProject: Readonly<Uint8Array>, scheduleSave = true, pushHistory = true) {
    if (this.state.projectHistory.length > 0) {
      const current = this.state.projectHistory[this.state.projectOffset];
      if (uint8ArraysEqual(serializedProject, current)) {
        return;
      }
    }

    const engine = this.engineProject;
    if (!engine) {
      return;
    }
    // Include stdlib model definitions so the editor can display and
    // navigate into stdlib modules. The save path does NOT pass
    // includeStdlib, so stdlib models are never persisted.
    const json = JSON.parse(await engine.serializeJson(undefined, true)) as JsonProject;
    let activeProject = await this.updateVariableErrors(projectFromJson(json));
    if (this.state.data) {
      activeProject = projectAttachData(activeProject, this.state.data, 'main');
    }
    // Preserve the latest optimistic view for the active model. This
    // updateProject call may have raced with a newer setView() (e.g. the
    // user kept panning while our engine round-trip was in flight), so the
    // engine snapshot we just rebuilt is potentially behind. Without this,
    // the rebuilt activeProject would snap the diagram back to the engine's
    // older view, then the next engine round-trip would catch up -- producing
    // the visible "pan jumps back and forth" effect.
    activeProject = preserveLiveView(activeProject, this.state.activeProject, this.state.modelName);

    const priorHistory = this.state.projectHistory.slice();

    // fractionally increase the version -- the server will only send back integer versions,
    // but this will ensure we can use a simple version check in the Canvas to invalidate caches.
    const projectVersion = this.state.projectVersion + 0.01;

    let projectHistory: readonly Readonly<Uint8Array>[];
    let projectOffset: number;
    if (pushHistory || priorHistory.length === 0) {
      projectHistory = [serializedProject, ...priorHistory].slice(0, MaxUndoSize);
      projectOffset = 0;
    } else {
      // View-only update: overwrite the current snapshot in place so the undo
      // stack is unchanged (no new step, nothing evicted).
      const replaced = priorHistory.slice();
      replaced[this.state.projectOffset] = serializedProject;
      projectHistory = replaced;
      projectOffset = this.state.projectOffset;
    }

    this.setState({
      projectHistory,
      activeProject,
      projectVersion,
      projectOffset,
    });
    if (scheduleSave) {
      this.scheduleSave();
    }
  }

  scheduleSave(): void {
    const { projectVersion } = this.state;

    setTimeout(async () => {
      await this.save(toInt(projectVersion));
    });
  }

  // Manual Save (and Retry): send the current model now rather than on the
  // next tick. With auto-save on this means "save now and confirm" -- it
  // re-sends even when nothing changed, so the user gets a fresh answer from
  // the server. If a save is already in flight it queues behind it like any
  // auto-save, and the status reflects whichever save finishes last.
  saveNow(): Promise<void> {
    return this.save(toInt(this.state.projectVersion));
  }

  handleSaveNow = (): void => {
    void this.saveNow();
  };

  // Saves the current engine state through the host's onSave and records the
  // outcome in saveStatus. 'saved' is set ONLY when onSave resolves with a
  // version (the server answered with success). A thrown error -- or onSave
  // resolving with no version -- is 'error', never assumed success. While a
  // queued save is pending the status stays 'saving' so an earlier result
  // doesn't flash before the latest edit is actually stored.
  async save(currVersion: number): Promise<void> {
    if (this.inSave) {
      this.saveQueued = true;
      return;
    }

    this.inSave = true;
    this.viewChangedDuringSave = false;
    if (this.showsSaveStatus()) {
      this.setState({ saveStatus: SaveSaving });
    }

    let version: number | undefined;
    let outcome: SaveStatus = SaveSaving;
    try {
      const engine = defined(this.engineProject);
      if (this.props.inputFormat === 'json') {
        version = await this.props.onSave({ format: 'json', data: await engine.serializeJson() }, currVersion);
      } else {
        version = await this.props.onSave({ format: 'protobuf', data: await engine.serializeProtobuf() }, currVersion);
      }
      if (version) {
        this.setState({ projectVersion: version });
        outcome = this.viewChangedDuringSave ? SaveUnsaved : SaveSaved;
      } else {
        outcome = { kind: 'error', reason: ReasonUnknown };
      }
    } catch (err) {
      // The reason on screen is plain words; the technical detail goes to
      // the console so it can be read from devtools or a bug report.
      console.error('save failed:', err instanceof SaveError ? err.detail : err);
      outcome = { kind: 'error', reason: reasonForError(err) };
      // Hosts that don't throw SaveError (simlin-serve's EditorHost throws
      // plain Errors with hand-written conflict/validation text) keep their
      // message in the error toast as before, since the status can only show
      // the generic reason for them. HostedWebEditor always throws SaveError,
      // so in the web app the status is the one place a failed save appears.
      if (!(err instanceof SaveError)) {
        this.setState((prevState: EditorState) => ({
          modelErrors: [...prevState.modelErrors, err as Error],
        }));
      }
    } finally {
      this.inSave = false;
      if (this.saveQueued) {
        this.saveQueued = false;
        // Use the new server version when available; fall back to
        // currVersion on error so the queued edit still attempts to flush
        // rather than being silently dropped. The queued save sets the
        // final status itself.
        await this.save(version ?? currVersion);
      } else if (this.showsSaveStatus()) {
        this.setState({ saveStatus: outcome });
      }
    }
  }

  // Read-only viewers and non-persisting hosts (the demo) get a fixed note in
  // the toolbar instead of a save status, so no status is ever recorded.
  showsSaveStatus(): boolean {
    return !this.props.readOnlyMode && this.props.persists !== false;
  }

  // A pan/zoom changed the stored view without sending a save (view updates
  // deliberately don't auto-save). A "Saved" status is no longer true, so it
  // becomes "Unsaved changes" and the Save button sends the new view. Saving
  // and Not saved are left as they are: an in-flight save is flagged so its
  // success reads as unsaved, and an error already says the model isn't saved.
  markViewUnsaved(): void {
    if (!this.showsSaveStatus()) {
      return;
    }
    if (this.inSave) {
      this.viewChangedDuringSave = true;
    } else if (this.state.saveStatus.kind === 'saved') {
      this.setState({ saveStatus: SaveUnsaved });
    }
  }

  appendModelError(msg: string) {
    this.setState((prevState: EditorState) => ({
      modelErrors: [...prevState.modelErrors, new EditorError(msg)],
    }));
  }

  handleDialClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
    this.setState({
      dialOpen: !this.state.dialOpen,
    });
  };

  handleDialClose = (_e: React.SyntheticEvent, reason: CloseReason) => {
    if (reason === 'mouseLeave' || reason === 'blur') {
      return;
    }
    // escapeKeyDown: close dial and clear tool
    this.setState({
      dialOpen: false,
      selectedTool: undefined,
    });
  };

  handleRename = async (oldName: string, newName: string) => {
    if (oldName === newName) {
      return;
    }

    const engine = this.engine();
    if (!engine) {
      return;
    }

    const project = defined(this.project());
    const model = project.models.get(this.state.modelName);
    const oldIdent = canonicalize(oldName);
    newName = newName.replace('\n', '\\n');
    const newIdent = canonicalize(newName);

    // Point every view's copy of the renamed element at the new name/ident. The
    // engine renames the *variable* but not the view elements, and CO-5(b) gives
    // the model two views (SFD + CLD), so rewrite the element in each. Computed
    // once and reused for both the engine patch and the live-state update below.
    const renamedViews: StockFlowView[] = (model?.views ?? []).map((v) => ({
      ...v,
      elements: v.elements.map((element: ViewElement) => {
        if (!isNamedViewElement(element) || element.ident !== oldIdent) {
          return element;
        }
        return { ...element, name: newName, ident: newIdent };
      }),
    }));

    // Rename in TWO sequential patches, not one combined patch.
    //
    // The engine validates every op in a patch against the model as it was
    // BEFORE that patch. A single patch that renames installed_base ->
    // installed_base_sdf AND upserts a view whose element now carries the new
    // ident is rejected: the upsertView's renamed element is resolved against the
    // pre-rename model, where installed_base_sdf does not exist yet, so the engine
    // returns DoesNotExist. Split it -- rename first, then move the view element --
    // and each op validates against a model in which the ident it references exists.
    try {
      await engine.applyPatch(
        {
          models: [
            {
              name: this.state.modelName,
              ops: [{ type: 'renameVariable', payload: { from: oldIdent, to: newIdent } }],
            },
          ],
        },
        { allowErrors: true },
      );

      if (renamedViews.length > 0) {
        const viewOps: JsonModelOperation[] = renamedViews.map((view, index) => ({
          type: 'upsertView',
          payload: { index, view: stockFlowViewToJson(view) },
        }));
        await engine.applyPatch({ models: [{ name: this.state.modelName, ops: viewOps }] }, { allowErrors: true });
      }
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      const msg = err.message ?? 'Unknown error during rename';
      this.appendModelError(msg);
      return;
    }

    // updateProject's preserveLiveView restores EVERY view from state.activeProject,
    // so the engine's renamed views (above) would be overwritten by the stale live
    // copies and the rename would visually revert. Push the renamed views into the
    // live project first; preserveLiveView then re-links them against the engine's
    // renamed variable and keeps the new label on the canvas.
    if (model && renamedViews.length > 0) {
      const renamedModel = { ...model, views: renamedViews };
      this.setState({
        activeProject: { ...project, models: mapSet(project.models, this.state.modelName, renamedModel) },
        flowStillBeingCreated: false,
      });
    } else {
      this.setState({ flowStillBeingCreated: false });
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  };

  handleSelection = (selection: ReadonlySet<UID>) => {
    this.setState({
      selection,
      flowStillBeingCreated: false,
      variableDetailsActiveTab: 0,
    });
    if (selection.size === 0) {
      this.setState({ showDetails: undefined });
    }
    // Defer one tick so React commits the new selection before the host
    // observes the callback. getSelectionIdents reads `this.state.selection`,
    // which is asynchronous in React 19 — calling it inline here would
    // return the prior selection. Reading inside the setTimeout closure
    // guarantees the call happens after the setState commit.
    //
    // Track the timer in an instance field so componentWillUnmount can
    // cancel it; a host that key-swaps the Editor (path change in
    // EditorHost) must not see this instance's idents land on the new
    // instance's path. A new selection in the same instance also
    // supersedes any pending deferral so the host always sees the
    // latest committed selection.
    const onSelectionChanged = this.props.onSelectionChanged;
    if (onSelectionChanged) {
      if (this.selectionDeferralTimer !== null) {
        clearTimeout(this.selectionDeferralTimer);
      }
      this.selectionDeferralTimer = setTimeout(() => {
        this.selectionDeferralTimer = null;
        onSelectionChanged(this.getSelectionIdents());
      }, 0);
    }
  };

  handleShowVariableDetails = () => {
    // Opening a variable snaps the rail to the Variable tab so the editor is
    // shown (rather than a stale Play/other tab from a prior selection).
    this.setState({ showDetails: 'variable', activeActivityTab: 'variable' });
  };

  handleActivityTabChange = (activeActivityTab: ActivityTab) => {
    this.setState({ activeActivityTab });
  };

  // ---- WS4 Phase 1c-iii: agent surface (imperative shell over agent-session) ----

  handleAgentSelectPersona = (persona: AgentPersona) => {
    this.setState({ agentSession: selectPersona(this.state.agentSession, persona) });
  };

  handleAgentSend = async (text: string): Promise<void> => {
    // Route by the active persona's action: Merlin builds (propose -> preview /
    // in-place apply), Athena describes (read-only). startSend appends the user
    // message + a working turn and marks busy; a cancel before the result resolves
    // wins (H2). Ids are generated in the shell, kept out of the pure core.
    const persona = this.state.agentSession.activePersona;
    const action = PERSONAS[persona].action;
    const agentTurnId = this.nextAgentId('a');
    // The prior conversation (before this send) is the context the agent carries;
    // the new prompt is sent separately, so exclude it here.
    const history = conversationContext(this.state.agentSession.conversation);
    this.setState({
      agentSession: startSend(this.state.agentSession, { userTurnId: this.nextAgentId('u'), agentTurnId }, text),
    });
    // Read the CURRENT (post-edit) model so an Athena describe following a Merlin
    // edit narrates the up-to-date structure -- the persona handoff (H3).
    const model = await this.currentModelJson();

    if (action === 'describe') {
      const describe = this.props.describe;
      const result: AgentResult = describe
        ? await describe({ model, modelName: this.state.modelName, history })
        : { kind: 'unavailable', reason: 'The scribe is unavailable right now.' };
      this.setState((prev) => ({ agentSession: applyResult(prev.agentSession, agentTurnId, { result }) }));
      return;
    }

    // action === 'build' (Merlin). Socrates/Seldon (coach/validate) are WS5.
    const propose = this.props.propose;
    if (action !== 'build' || !propose) {
      this.setState((prev) => ({
        agentSession: applyResult(prev.agentSession, agentTurnId, {
          result: { kind: 'unavailable', reason: 'That assistant is unavailable right now.' },
        }),
      }));
      return;
    }
    const result = await propose({ prompt: text, model, modelName: this.state.modelName, history });
    const inPlace = canApplyInPlace(this.props.agentTier ?? Tier.Public);
    const { session, apply } = resolveAndMaybeApply(this.state.agentSession, agentTurnId, result, inPlace);
    this.setState({ agentSession: session });
    if (apply) {
      await this.applyProposedModel(apply.model, apply.proposal);
    }
  };

  handleAgentCancel = (turnId: string) => {
    this.setState((prev) => ({ agentSession: cancelAgentSession(prev.agentSession, turnId) }));
  };

  handleAgentDecide = async (turnId: string, decision: 'accepted' | 'rejected'): Promise<void> => {
    const turn = findTurn(this.state.agentSession.conversation, turnId);
    if (!turn || turn.role !== 'agent' || turn.state.status !== 'proposal') {
      return;
    }
    const proposalId = turn.state.proposal.proposalId;
    const { session, apply } = decideAgentSession(this.state.agentSession, turnId, proposalId, decision);
    this.setState({ agentSession: session });
    if (apply) {
      await this.applyProposedModel(apply.model, apply.proposal);
    }
  };

  /** Read the current active model as a JsonModel from the engine (the agent's input). */
  private async currentModelJson(): Promise<JsonModel> {
    const engine = defined(this.engine());
    const proj = JSON.parse(await engine.serializeJson()) as JsonProject;
    const found = proj.models.find((m) => m.name === this.state.modelName) ?? proj.models[0];
    return found ?? { name: this.state.modelName, stocks: [], flows: [], auxiliaries: [] };
  }

  /**
   * Apply an accepted proposal to the canvas: upsert only the variables it added
   * or changed (looked up in the proposed snapshot), delete only the ones it
   * removed, incrementally auto-layout the result, and commit it undoably through
   * updateProject. Applying only the diff -- not blanket-replacing the model --
   * means a concurrent manual edit made while the agent was generating is not
   * clobbered (H2), and the agent's removals are exact.
   *
   * preserveLiveView reconciliation (same class as the rename fix): updateProject
   * rebuilds activeProject from the engine but then restores the active model's
   * views from state.activeProject. syncLayout just placed the proposal's NEW
   * variables into the engine's views, but those elements are absent from the stale
   * live views -- so a plain updateProject would drop them and the added variables
   * would never appear on the canvas. We push the engine's fresh (post-layout)
   * views into the live project first; preserveLiveView then re-links THOSE views
   * (which include the new elements) against the engine's variables, so the applied
   * change and its layout survive.
   */
  private async applyProposedModel(model: JsonModel, proposal: ProposalSummary): Promise<void> {
    const engine = this.engine();
    if (!engine) {
      return;
    }
    const modelName = this.state.modelName;
    const upsert = new Set<string>([...proposal.added, ...proposal.changed]);
    const ops: JsonModelOperation[] = [];
    for (const s of model.stocks) {
      if (upsert.has(s.name)) {
        ops.push({ type: 'upsertStock', payload: { stock: s } });
      }
    }
    for (const f of model.flows) {
      if (upsert.has(f.name)) {
        ops.push({ type: 'upsertFlow', payload: { flow: f } });
      }
    }
    for (const a of model.auxiliaries) {
      if (upsert.has(a.name)) {
        ops.push({ type: 'upsertAux', payload: { aux: a } });
      }
    }
    for (const ident of proposal.removed) {
      ops.push({ type: 'deleteVariable', payload: { ident } });
    }
    if (ops.length === 0) {
      return;
    }
    const patch: JsonProjectPatch = { models: [{ name: modelName, ops }] };
    try {
      await engine.applyPatch(patch, { allowErrors: true });
      // Incremental layout: keeps existing element positions, places only the new
      // variables (passing the patch enables the incremental code path).
      await engine.syncLayout(modelName, patch);
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      this.appendModelError(err.message ?? 'Unknown error applying the proposed model');
      return;
    }

    // Push the engine's fresh views (with the newly placed elements) into the live
    // project so preserveLiveView preserves them rather than the stale live views.
    const freshJson = JSON.parse(await engine.serializeJson(undefined, true)) as JsonProject;
    const freshProject = projectFromJson(freshJson);
    const freshModel = freshProject.models.get(modelName);
    const liveProject = this.project();
    if (freshModel && liveProject) {
      const liveModel = liveProject.models.get(modelName);
      if (liveModel) {
        const mergedModel = { ...liveModel, views: freshModel.views };
        this.setState({
          activeProject: { ...liveProject, models: mapSet(liveProject.models, modelName, mergedModel) },
        });
      }
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  }

  handleViewModeChange = (viewMode: ViewMode) => {
    // Build/Play work on the SFD diagram; Loops shows the causal-loop view (where
    // the interactive loop highlight lives). The rail's active tab must track the
    // mode: Play opens the Play tab, and leaving Play (Build/Loops) resets to the
    // Variable tab -- otherwise the Play sliders linger in the rail after
    // switching back to Build (NC-3, mode/panel desync).
    const renderMode: RenderMode = viewMode === 'loops' ? 'cld' : 'sfd';
    const activeActivityTab: ActivityTab = viewMode === 'play' ? 'play' : 'variable';
    this.setState({ viewMode, renderMode, activeActivityTab }, () => {
      // CO-5(b): entering Loops materializes the CLD view (views[1]) if it doesn't
      // exist yet, so the mode reads/writes its own persisted ring layout. Fit on
      // entry here (a user mode switch), NOT inside ensureCldView (which the
      // refetch calls repeatedly -> would loop). When the view is created fresh,
      // ensureCldView's create path fits it once the ring layout lands.
      if (viewMode === 'loops') {
        void this.ensureCldView();
        this.fitCurrentView();
      }
    });
  };

  handleResultsBandLayoutChange = (resultsBandLayout: BandLayout) => {
    this.setState({ resultsBandLayout });
  };

  handleZoneResize = (next: { activitiesWidth?: number; resultsHeight?: number }) => {
    // Clamp to the zone minimums so a drag can never collapse a rail/band to zero
    // and strand it (the WS8a punch-list "Results won't reopen / side toolbar
    // vanishes" root -- a dragged-away zone had no way back).
    this.setState((prev) => ({
      zoneActivitiesWidth:
        next.activitiesWidth !== undefined
          ? Math.max(MIN_ACTIVITIES_WIDTH, next.activitiesWidth)
          : prev.zoneActivitiesWidth,
      zoneResultsHeight:
        next.resultsHeight !== undefined
          ? Math.max(MIN_RESULTS_HEIGHT, next.resultsHeight)
          : prev.zoneResultsHeight,
    }));
  };

  // Switching density re-applies the preset's base zone sizes (the sliver height
  // is read live from densityConfig in render). Drag-resize then fine-tunes the
  // rail/band from this baseline until the next preset switch.
  handleDensityChange = (density: DensityPreset) => {
    const cfg = densityConfig(density);
    this.setState({
      density,
      zoneActivitiesWidth: cfg.activitiesWidth,
      zoneResultsHeight: cfg.resultsHeight,
    });
  };

  handlePaperChange = (canvasPaper: CanvasPaper) => {
    this.setState({ canvasPaper });
  };

  handlePlayOverrideChange = (ident: string, value: number) => {
    const playOverrides = new Map(this.state.playOverrides);
    playOverrides.set(ident, value);
    this.setState({ playOverrides });
  };

  handlePlayResimulate = async () => {
    const engine = this.engine();
    if (!engine) {
      return;
    }
    // Button-driven (WS2 decision): re-run mainModel with the Play overrides and
    // update state.data, so behavior (sparklines now, the Results band later)
    // reflects the moved levers.
    this.setState({ playSimulating: true });
    try {
      await this.loadSim(engine, Object.fromEntries(this.state.playOverrides));
    } finally {
      this.setState({ playSimulating: false });
    }
  };

  handleLoopHover = (id: string, hovering: boolean) => {
    this.setState({ hoveredLoopId: hovering ? id : undefined });
  };

  handleLoopSelect = (id: string) => {
    // Click pins/unpins a loop's highlight so it persists after the pointer
    // leaves the badge. CO-5: several loops can be pinned at once, so this
    // toggles membership in a set rather than swapping a single id. Hovering
    // another loop still previews on top.
    this.setState((prev) => {
      const next = new Set(prev.pinnedLoopIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { pinnedLoopIds: next };
    });
  };

  /**
   * The set of loops emphasised on the canvas: every click-pinned loop plus the
   * transiently hovered one (CO-5 persistent multi-highlight). Recomputed each
   * render from the two pieces of highlight state.
   */
  private highlightedLoopIdSet(): Set<string> {
    const ids = new Set(this.state.pinnedLoopIds);
    if (this.state.hoveredLoopId !== undefined) {
      ids.add(this.state.hoveredLoopId);
    }
    return ids;
  }

  getLatexEquation = async (ident: string): Promise<string | undefined> => {
    const engine = this.engine();
    if (!engine) return undefined;
    try {
      const model = await engine.getModel(this.state.modelName);
      return (await model.getLatexEquation(ident)) ?? undefined;
    } catch {
      return undefined;
    }
  };

  handleSelectionDelete = async () => {
    const selection = this.state.selection;
    const { modelName } = this.state;
    const view = defined(this.getView());

    // this will remove the selected elements, clouds, and connectors
    let elements = view.elements.filter((element: ViewElement) => {
      const remove =
        selection.has(element.uid) ||
        (element.type === 'cloud' && selection.has(element.flowUid)) ||
        (element.type === 'link' &&
          (selection.has(element.toUid) || selection.has(element.fromUid)));
      return !remove;
    });

    // next we have to potentially make new clouds if we've deleted a stock
    let { nextUid } = view;
    const clouds: CloudViewElement[] = [];
    elements = elements.map((element: ViewElement) => {
      if (element.type !== 'flow') {
        return element;
      }
      const points = element.points.map((pt) => {
        if (!pt.attachedToUid || !selection.has(pt.attachedToUid)) {
          return pt;
        }

        const cloud: CloudViewElement = {
          type: 'cloud',
          uid: nextUid++,
          x: pt.x,
          y: pt.y,
          flowUid: element.uid,
          isZeroRadius: false,
          ident: undefined,
        };

        clouds.push(cloud);

        return { ...pt, attachedToUid: cloud.uid };
      });
      return { ...element, points };
    });
    elements = [...elements, ...clouds];

    const engine = this.engine();
    if (!engine) {
      return;
    }

    const deleteOps: JsonModelOperation[] = this.getSelectionIdents().map((ident) => ({
      type: 'deleteVariable' as const,
      payload: { ident },
    }));

    if (deleteOps.length > 0) {
      const patch: JsonProjectPatch = {
        models: [{ name: modelName, ops: deleteOps }],
      };
      try {
        await engine.applyPatch(patch, { allowErrors: true });
      } catch (e: unknown) {
        const err = getErrorDetails(e);
        console.error('applyPatch error (delete):', err.code, err.message, err.details);
        this.appendModelError(err.message ?? 'Unknown error during delete');
      }
    }

    await this.updateView({ ...view, elements, nextUid });
    this.setState({
      selection: new Set<number>(),
    });
    this.scheduleSimRun();
  };

  handleMoveLabel = async (uid: UID, side: 'top' | 'left' | 'bottom' | 'right') => {
    const view = defined(this.getView());

    const elements = view.elements.map((element: ViewElement) => {
      if (element.uid !== uid || !isNamedViewElement(element)) {
        return element;
      }
      return { ...element, labelSide: side };
    });

    await this.updateView({ ...view, elements });
  };

  handleFlowAttach = async (
    flow: FlowViewElement,
    targetUid: number,
    cursorMoveDelta: Point,
    fauxTargetCenter: Point | undefined,
    inCreation: boolean,
    isSourceAttach?: boolean,
  ) => {
    let { selection } = this.state;
    const view = defined(this.getView());

    let isCreatingNew = false;
    let stockDetachingIdent: string | undefined;
    let stockAttachingIdent: string | undefined;
    let sourceStockIdent: string | undefined;
    let sourceStockDetachingIdent: string | undefined;
    let sourceStockAttachingIdent: string | undefined;
    let uidToDelete: number | undefined;
    let updatedCloud: ViewElement | undefined;
    const newClouds: ViewElement[] = [];

    let nextUid = view.nextUid;
    const getUid = (uid: number) => {
      for (const e of view.elements) {
        if (e.uid === uid) {
          return e;
        }
      }
      throw new Error(`unknown uid ${uid}`);
    };

    let elements = view.elements.map((element: ViewElement) => {
      if (element.uid !== flow.uid) {
        return element;
      }
      if (element.type !== 'flow') {
        return element;
      }

      if (isSourceAttach) {
        // Handle source attachment (first point)
        const oldFrom = getUid(defined(first(element.points).attachedToUid));
        let newCloud = false;
        let updateCloud = false;
        let from: StockViewElement | CloudViewElement;

        if (targetUid) {
          if (oldFrom.type === 'cloud') {
            uidToDelete = oldFrom.uid;
          }
          const newTarget = getUid(targetUid);
          if (newTarget.type !== 'stock' && newTarget.type !== 'cloud') {
            throw new Error(`new target isn't a stock or cloud (uid ${newTarget.uid})`);
          }
          from = newTarget;
        } else if (oldFrom.type === 'cloud') {
          updateCloud = true;
          from = {
            ...oldFrom,
            x: oldFrom.x - cursorMoveDelta.x,
            y: oldFrom.y - cursorMoveDelta.y,
          };
        } else {
          // Detaching from a stock - create a new cloud at the release position.
          // Use the same approach as the sink path: oldFrom.x - cursorMoveDelta.x/y
          // This ensures the cloud appears where the user released, not where they started.
          newCloud = true;
          from = {
            type: 'cloud' as const,
            uid: nextUid++,
            x: oldFrom.x - cursorMoveDelta.x,
            y: oldFrom.y - cursorMoveDelta.y,
            flowUid: flow.uid,
            isZeroRadius: false,
            ident: undefined,
          };
        }

        if (oldFrom.uid !== from.uid) {
          if (oldFrom.type === 'stock') {
            sourceStockDetachingIdent = oldFrom.ident;
          }
          if (from.type === 'stock') {
            sourceStockAttachingIdent = from.ident;
          }
        }

        const moveDelta = {
          x: oldFrom.x - from.x,
          y: oldFrom.y - from.y,
        };
        const points = element.points.map((point) => {
          if (point.attachedToUid !== oldFrom.uid) {
            return point;
          }
          return { ...point, attachedToUid: from.uid };
        });
        from = {
          ...from,
          x: oldFrom.x,
          y: oldFrom.y,
        } as StockViewElement | CloudViewElement;
        element = { ...element, points };

        [from, element] = UpdateCloudAndFlow(from, element as FlowViewElement, moveDelta);
        if (newCloud) {
          newClouds.push(from);
        } else if (updateCloud) {
          updatedCloud = from;
        }

        return element;
      }

      // Handle sink attachment (last point) - original behavior
      const oldTo = getUid(defined(last(element.points).attachedToUid));
      let newCloud = false;
      let updateCloud = false;
      let to: StockViewElement | CloudViewElement;
      if (targetUid) {
        if (oldTo.type === 'cloud') {
          uidToDelete = oldTo.uid;
        }
        const newTarget = getUid(targetUid);
        if (newTarget.type !== 'stock' && newTarget.type !== 'cloud') {
          throw new Error(`new target isn't a stock or cloud (uid ${newTarget.uid})`);
        }
        to = newTarget;
      } else if (oldTo.type === 'cloud') {
        updateCloud = true;
        to = {
          ...oldTo,
          x: oldTo.x - cursorMoveDelta.x,
          y: oldTo.y - cursorMoveDelta.y,
        };
      } else {
        newCloud = true;
        to = {
          type: 'cloud' as const,
          uid: nextUid++,
          x: oldTo.x - cursorMoveDelta.x,
          y: oldTo.y - cursorMoveDelta.y,
          flowUid: flow.uid,
          isZeroRadius: false,
          ident: undefined,
        };
      }

      if (oldTo.uid !== to.uid) {
        if (oldTo.type === 'stock') {
          stockDetachingIdent = oldTo.ident;
        }
        if (to.type === 'stock') {
          stockAttachingIdent = to.ident;
        }
      }

      const moveDelta = {
        x: oldTo.x - to.x,
        y: oldTo.y - to.y,
      };
      const points = element.points.map((point) => {
        if (point.attachedToUid !== oldTo.uid) {
          return point;
        }
        return { ...point, attachedToUid: to.uid };
      });
      to = {
        ...to,
        x: oldTo.x,
        y: oldTo.y,
      } as StockViewElement | CloudViewElement;
      element = { ...element, points };

      [to, element] = UpdateCloudAndFlow(to, element as FlowViewElement, moveDelta);
      if (newCloud) {
        newClouds.push(to);
      } else if (updateCloud) {
        updatedCloud = to;
      }

      return element;
    });
    // we might have updated some clouds
    elements = elements.map((element: ViewElement) => {
      if (updatedCloud && updatedCloud.uid === element.uid) {
        return updatedCloud;
      }
      return element;
    });
    // if we have something to delete, do it here
    elements = elements.filter((e) => e.uid !== uidToDelete);
    if (flow.uid === inCreationUid) {
      flow = {
        ...flow,
        uid: nextUid++,
      };
      const firstPt = first(flow.points);
      const sourceUid = firstPt.attachedToUid;
      if (sourceUid === inCreationCloudUid) {
        const newCloud: CloudViewElement = {
          type: 'cloud',
          uid: nextUid++,
          x: firstPt.x,
          y: firstPt.y,
          flowUid: flow.uid,
          isZeroRadius: false,
          ident: undefined,
        };
        elements = [...elements, newCloud];
        flow = {
          ...flow,
          points: flow.points.map((pt) => {
            if (pt.attachedToUid === inCreationCloudUid) {
              return { ...pt, attachedToUid: newCloud.uid };
            }
            return pt;
          }),
        };
      } else if (sourceUid) {
        const sourceStock = getUid(sourceUid) as StockViewElement;
        sourceStockIdent = defined(sourceStock.ident);
      }
      const lastPt = last(flow.points);
      if (lastPt.attachedToUid === fauxCloudTargetUid) {
        let newCloud = false;
        let to: StockViewElement | CloudViewElement;
        if (targetUid) {
          to = getUid(targetUid) as StockViewElement | CloudViewElement;
          stockAttachingIdent = defined(to.ident);
          cursorMoveDelta = {
            x: 0,
            y: 0,
          };
        } else {
          to = {
            type: 'cloud' as const,
            uid: nextUid++,
            x: defined(fauxTargetCenter).x,
            y: defined(fauxTargetCenter).y,
            flowUid: flow.uid,
            isZeroRadius: false,
            ident: undefined,
          };
          newCloud = true;
        }
        flow = {
          ...flow,
          points: flow.points.map((pt) => {
            if (pt.attachedToUid === fauxCloudTargetUid) {
              return { ...pt, attachedToUid: to.uid };
            }
            return pt;
          }),
        };
        [to, flow] = UpdateCloudAndFlow(to, flow, cursorMoveDelta);
        if (newCloud) {
          elements = [...elements, to];
        }
      }
      elements = [...elements, flow];
      selection = new Set([flow.uid]);
      isCreatingNew = true;
    }
    elements = [...elements, ...newClouds];

    const engine = this.engine();
    if (!engine) {
      return;
    }

    const ops: JsonModelOperation[] = [];

    if (isCreatingNew) {
      ops.push({
        type: 'upsertFlow',
        payload: {
          flow: {
            name: (flow as NamedViewElement).name,
            equation: '',
          },
        },
      });
    }

    if (sourceStockIdent) {
      const model = defined(this.getModel());
      const stockVar = model.variables.get(sourceStockIdent);
      if (stockVar?.type === 'stock') {
        ops.push({
          type: 'updateStockFlows',
          payload: {
            ident: stockVar.ident,
            inflows: [...stockVar.inflows],
            outflows: [...stockVar.outflows, flow.ident],
          },
        });
      }
    }

    // Handle source stock attaching (outflows)
    if (sourceStockAttachingIdent) {
      const model = defined(this.getModel());
      const stockVar = model.variables.get(sourceStockAttachingIdent);
      if (stockVar?.type === 'stock') {
        ops.push({
          type: 'updateStockFlows',
          payload: {
            ident: stockVar.ident,
            inflows: [...stockVar.inflows],
            outflows: [...stockVar.outflows, flow.ident],
          },
        });
      }
    }

    // Handle source stock detaching (outflows)
    if (sourceStockDetachingIdent) {
      const model = defined(this.getModel());
      const stockVar = model.variables.get(sourceStockDetachingIdent);
      if (stockVar?.type === 'stock') {
        ops.push({
          type: 'updateStockFlows',
          payload: {
            ident: stockVar.ident,
            inflows: [...stockVar.inflows],
            outflows: stockVar.outflows.filter((f) => f !== flow.ident),
          },
        });
      }
    }

    // Handle sink stock attaching (inflows)
    if (stockAttachingIdent) {
      const model = defined(this.getModel());
      const stockVar = model.variables.get(stockAttachingIdent);
      if (stockVar?.type === 'stock') {
        ops.push({
          type: 'updateStockFlows',
          payload: {
            ident: stockVar.ident,
            inflows: [...stockVar.inflows, flow.ident],
            outflows: [...stockVar.outflows],
          },
        });
      }
    }

    // Handle sink stock detaching (inflows)
    if (stockDetachingIdent) {
      const model = defined(this.getModel());
      const stockVar = model.variables.get(stockDetachingIdent);
      if (stockVar?.type === 'stock') {
        ops.push({
          type: 'updateStockFlows',
          payload: {
            ident: stockVar.ident,
            inflows: stockVar.inflows.filter((f) => f !== flow.ident),
            outflows: [...stockVar.outflows],
          },
        });
      }
    }

    if (ops.length > 0) {
      const patch: JsonProjectPatch = {
        models: [{ name: this.state.modelName, ops }],
      };
      try {
        await engine.applyPatch(patch, { allowErrors: true });
      } catch (e: unknown) {
        const err = getErrorDetails(e);
        console.error('applyPatch error (flow attach):', err.code, err.message, err.details);
        this.appendModelError(err.message ?? 'Unknown error during flow attach');
        this.setState({ selection, flowStillBeingCreated: inCreation });
        return;
      }
    }

    await this.updateView({ ...view, nextUid, elements });
    this.setState({
      selection,
      flowStillBeingCreated: inCreation,
    });
    this.scheduleSimRun();
  };

  handleLinkAttach = async (link: LinkViewElement, newTarget: string) => {
    let { selection } = this.state;
    let view = defined(this.getView());

    const getName = (ident: string) => {
      for (const e of view.elements) {
        if (isNamedViewElement(e) && e.ident === ident) {
          return e;
        }
      }
      throw new Error(`unknown name ${ident}`);
    };

    let nextUid = view.nextUid;
    let elements: ViewElement[];
    if (link.uid === inCreationUid) {
      const to = getName(newTarget);
      const newLink: LinkViewElement = {
        ...link,
        uid: nextUid++,
        toUid: to.uid,
      };
      elements = [...view.elements, newLink];
      selection = new Set([newLink.uid]);
    } else {
      // Reattachment: Canvas already computed the correct arc in
      // link.arc, so we just update the target.
      const to = getName(defined(newTarget));
      elements = view.elements.map((element: ViewElement) => {
        if (element.uid !== link.uid || element.type !== 'link') {
          return element;
        }
        return { ...element, arc: link.arc, toUid: to.uid };
      });
    }
    view = { ...view, nextUid, elements };

    await this.updateView(view);
    this.setState({ selection });
  };

  async updateView(view: StockFlowView) {
    // Optimistic update: reflect the new view in state immediately so the
    // UI never flashes stale positions while the async engine round-trip
    // (applyPatch + serialize) completes.
    this.setView(view);
    this.setState({ projectVersion: this.state.projectVersion + 0.001 });

    const engine = this.engine();
    if (engine) {
      const ops: JsonModelOperation[] = [
        {
          type: 'upsertView',
          payload: { index: this.activeViewIndex(), view: stockFlowViewToJson(view) },
        },
      ];
      const patch: JsonProjectPatch = {
        models: [{ name: this.state.modelName, ops }],
      };
      try {
        await engine.applyPatch(patch, { allowErrors: true });
      } catch (e: unknown) {
        const err = getErrorDetails(e);
        console.error('applyPatch error (view update):', err.code, err.message, err.details);
        const msg = err.message ?? 'Unknown error during view update';
        this.appendModelError(msg);
        return;
      }
      await this.updateProject(await engine.serializeProtobuf());
    }
  }

  handleCreateVariable = async (element: ViewElement) => {
    const view = defined(this.getView());
    const engine = this.engine();
    if (!engine) {
      return;
    }

    let nextUid = view.nextUid;
    const elements = [...view.elements, { ...element, uid: nextUid++ }];
    const elementType = viewElementType(element);
    const name = (element as NamedViewElement).name;

    let op: JsonModelOperation;
    if (elementType === 'stock') {
      op = {
        type: 'upsertStock',
        payload: {
          stock: {
            name,
            inflows: [],
            outflows: [],
            initialEquation: '',
          },
        },
      };
    } else if (elementType === 'flow') {
      op = {
        type: 'upsertFlow',
        payload: {
          flow: {
            name,
            equation: '',
          },
        },
      };
    } else if (elementType === 'module') {
      op = {
        type: 'upsertModule',
        payload: {
          module: {
            name,
            modelName: '',
            references: [],
          },
        },
      };
    } else {
      op = {
        type: 'upsertAux',
        payload: {
          aux: {
            name,
            equation: '',
          },
        },
      };
    }

    // AC5.2: patch targets this.state.modelName (not a hardcoded value), so
    // module creation works at any nesting depth -- navigating into a child
    // model updates modelName, and newly created modules land in that child.
    const patch: JsonProjectPatch = {
      models: [{ name: this.state.modelName, ops: [op] }],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (variable creation):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error during variable creation');
      // The engine rejected the variable creation (e.g. the name
      // collides with an existing variable, or the project is
      // read-only).  We must NOT update the local view in that
      // case -- otherwise the view ends up with a ghost element
      // that has no engine counterpart, and any subsequent
      // operation on it (rename, delete, equation edit) fails with
      // `does_not_exist` because the engine truly never knew about
      // it.  handleRename and friends already follow this
      // commit-only-on-success pattern; bringing handleCreateVariable
      // in line with them.
      return;
    }

    await this.updateView({ ...view, nextUid, elements });
    this.setState({
      selection: new Set<number>(),
    });
  };

  handleSelectionMove = async (delta: Point, arcPoint?: Point, segmentIndex?: number) => {
    const view = defined(this.getView());
    const selection = this.state.selection;

    const { updatedElements } = applyGroupMovement({
      elements: view.elements,
      selection,
      delta,
      arcPoint,
      segmentIndex,
    });

    const elements = view.elements.map((el) => updatedElements.get(el.uid) ?? el);
    await this.updateView({ ...view, elements });
  };

  handleDrawerToggle = (isOpen: boolean) => {
    this.setState({
      drawerOpen: isOpen,
    });
  };

  async applySimSpecChange(updates: Partial<JsonSimSpecs>) {
    const engine = this.engine();
    if (!engine) {
      return;
    }

    const project = this.project();
    if (!project) {
      return;
    }

    const simSpec = project.simSpecs;
    const dt = simSpec.dt.isReciprocal ? `1/${simSpec.dt.value}` : `${simSpec.dt.value}`;

    // Convert saveStep Dt to the actual numeric step size
    let saveStep: number | undefined;
    if (simSpec.saveStep) {
      saveStep = simSpec.saveStep.isReciprocal ? 1 / simSpec.saveStep.value : simSpec.saveStep.value;
    }

    const simSpecs: JsonSimSpecs = {
      startTime: updates.startTime ?? simSpec.start,
      endTime: updates.endTime ?? simSpec.stop,
      dt: updates.dt ?? dt,
      timeUnits: updates.timeUnits ?? simSpec.timeUnits,
      saveStep: updates.saveStep ?? saveStep,
      method: updates.method ?? simSpec.simMethod,
    };

    const patch: JsonProjectPatch = {
      projectOps: [
        {
          type: 'setSimSpecs',
          payload: { simSpecs: simSpecs },
        },
      ],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (sim specs):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error updating sim specs');
      return;
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  }

  handleStartTimeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    await this.applySimSpecChange({ startTime: value });
  };

  handleStopTimeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    await this.applySimSpecChange({ endTime: value });
  };

  handleDtChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    await this.applySimSpecChange({ dt: `${value}` });
  };

  handleTimeUnitsChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    await this.applySimSpecChange({ timeUnits: value });
  };

  handleDownloadXmile = async () => {
    const engine = this.engine();
    if (!engine) {
      return;
    }
    try {
      const xmile = await engine.toXmileString();
      const encoder = new TextEncoder();
      const xmileBytes = encoder.encode(xmile);
      const blob = new Blob([xmileBytes], {
        type: 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      document.body.appendChild(a);
      try {
        a.style.display = 'none';
      } catch {
        // oh well
      }
      a.href = url;
      a.download = `${this.props.name}-${this.state.projectVersion | 0}.stmx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const details = getErrorDetails(err);
      if (details.message) {
        this.appendModelError(details.message);
      }
    }
  };

  getDrawer() {
    const project = this.project();
    if (!project || this.props.embedded) {
      return;
    }

    const model = project.models.get(this.state.modelName);
    if (!model) {
      return;
    }

    const simSpec = project.simSpecs;
    const dt = simSpec.dt.isReciprocal ? 1 / simSpec.dt.value : simSpec.dt.value;

    return (
      <ModelPropertiesDrawer
        modelName={project.name}
        open={this.state.drawerOpen}
        onDrawerToggle={this.handleDrawerToggle}
        startTime={simSpec.start}
        stopTime={simSpec.stop}
        dt={dt}
        timeUnits={simSpec.timeUnits || ''}
        onStartTimeChange={this.handleStartTimeChange}
        onStopTimeChange={this.handleStopTimeChange}
        onDtChange={this.handleDtChange}
        onTimeUnitsChange={this.handleTimeUnitsChange}
        onDownloadXmile={this.handleDownloadXmile}
        onOpenModel={this.props.onOpenModel}
      />
    );
  }

  getModel(): Model | undefined {
    const project = this.project();
    if (!project) {
      return;
    }
    const modelName = this.state.modelName;
    return project.models.get(modelName);
  }

  /**
   * Which view the current mode reads/writes. SFD (Build/Test) uses views[0]; the
   * Loops (CLD) mode uses its OWN persisted view, views[1] (CO-5(b) -- "one model,
   * many views"), so the CLD keeps clean ring positions while the SFD keeps its
   * stock-flow layout, each independently editable.
   */
  private activeViewIndex(): number {
    return this.state.viewMode === 'loops' ? 1 : 0;
  }

  getView(): StockFlowView | undefined {
    const project = this.project();
    if (!project) {
      return;
    }
    const modelName = this.state.modelName;
    const model = project.models.get(modelName);
    if (!model) {
      return;
    }

    // Fall back to views[0] during the brief window before the CLD view exists
    // (it's created lazily on entering Loops).
    return model.views[this.activeViewIndex()] ?? model.views[0];
  }

  setView(view: StockFlowView): void {
    const project = defined(this.project());
    const model = defined(project.models.get(this.state.modelName));
    const views = [...model.views];
    views[this.activeViewIndex()] = view;
    const updatedModel = { ...model, views };
    const activeProject = { ...project, models: mapSet(project.models, this.state.modelName, updatedModel) };
    this.setState({ activeProject });
  }

  async queueViewUpdate(view: StockFlowView): Promise<void> {
    // Optimistic update: same pattern as updateView — reflect the new
    // viewBox/zoom in state immediately to avoid a frame of flicker.
    this.setView(view);
    this.setState({ projectVersion: this.state.projectVersion + 0.001 });

    const engine = this.engine();
    if (engine) {
      const ops: JsonModelOperation[] = [
        {
          type: 'upsertView',
          payload: { index: this.activeViewIndex(), view: stockFlowViewToJson(view) },
        },
      ];
      const patch: JsonProjectPatch = {
        models: [{ name: this.state.modelName, ops }],
      };
      try {
        await engine.applyPatch(patch, { allowErrors: true });
      } catch (e: unknown) {
        const err = getErrorDetails(e);
        console.error('applyPatch error (queue view update):', err.code, err.message, err.details);
        const msg = err.message ?? 'Unknown error during view update';
        this.appendModelError(msg);
        return;
      }

      // View-only update: don't add an undo step (pushHistory=false), so pans/
      // zooms/layout tweaks never evict a real edit from the undo stack.
      await this.updateProject(await engine.serializeProtobuf(), false, false);
      this.markViewUnsaved();
    } else {
      // there exists a race where we need to center/update the viewBox when
      // displaying a newly imported model, but the async wasm stuff doesn't
      // complete before we want to save the viewBox change.  In this case
      // set a flag we check when finalizing installation of the new engine.
      this.newEngineShouldPullView = true;
      this.newEngineQueuedView = view;
    }
  }

  handleViewBoxChange = async (viewBox: Rect, zoom: number) => {
    const view = defined(this.getView());
    await this.queueViewUpdate({ ...view, viewBox, zoom });
  };

  async centerVariable(element: ViewElement): Promise<void> {
    const view = defined(this.getView());
    const zoom = view.zoom;

    const cx = element.x;
    const cy = element.y;

    const viewCy = view.viewBox.height / 2 / zoom;
    const viewCx = (view.viewBox.width - SearchbarWidthSm) / 2 / zoom;

    const viewBox: Rect = {
      ...view.viewBox,
      x: viewCx - cx,
      y: viewCy - cy,
    };

    await this.queueViewUpdate({ ...view, viewBox });
  }

  // CO-5(b): the engine's causal links (from -> to + sign), captured on each
  // analysis refetch, used to seed the CLD view's links (including the stock<->flow
  // edges the SFD draws as pipes, so the balancing loops show their '-').
  // Guards against building the CLD view twice while the first create is in flight.
  private creatingCldView = false;
  // Ref to the Canvas so we can trigger fit-to-view (CO-5 #1) on entering the CLD
  // and from the fit control.
  private canvasRef = React.createRef<Canvas>();

  /**
   * CO-5 #1: fit-and-center the current view. Deferred a tick so any pending
   * view/render (e.g. a freshly created CLD view) is committed before the Canvas
   * measures element bounds.
   */
  private fitCurrentView(): void {
    setTimeout(() => {
      this.canvasRef.current?.fitToView();
    }, 0);
  }

  /**
   * CO-5(b): ensure the Loops (CLD) view exists as its own persisted view
   * (views[1]). Built ONCE from the SFD view with the loop members arranged as
   * rings; after that it persists, so re-entering Loops never clobbers a user's
   * dragged positions. No-op if a CLD view already exists or no loops are detected
   * yet.
   */
  /**
   * CO-5 multi-loop layout (Williams 2026-08-12 standard): place the CLD's loop
   * members with the layout engine we already own (`generate_best_layout`, via the
   * autoLayout facade) instead of the hand-rolled per-loop `ringLayout`, which
   * collapses shared-hub loops into a straight chord. The spike confirmed the owned
   * engine separates shared-hub loops on the Bass-class floor (logistic-growth:
   * 44 deg / 84 deg apart). We strip the stored views first so the engine computes a
   * FRESH force-directed layout (loop-legible placement, not the SFD re-render), then
   * keep only the loop members' positions. Returns undefined -- caller falls back to
   * ringLayout -- when the engine can't position every member, so a partial layout
   * never ships.
   */
  private async enginePlacement(
    members: ReadonlySet<string>,
  ): Promise<Map<string, { x: number; y: number }> | undefined> {
    const engine = this.engine();
    if (!engine) {
      return undefined;
    }
    try {
      const raw = JSON.parse(await engine.serializeJson()) as { models: Array<{ views?: unknown[] }> };
      const stripped = { ...raw, models: raw.models.map((m) => ({ ...m, views: [] })) };
      const laidOut = await autoLayout(stripped, { modelName: this.state.modelName });
      const models = laidOut.models as
        | Array<{ views?: Array<{ elements?: Array<{ ident?: string; name?: string; x?: number; y?: number }> }> }>
        | undefined;
      const elems = models?.[0]?.views?.[0]?.elements ?? [];
      const out = new Map<string, { x: number; y: number }>();
      for (const el of elems) {
        const ident =
          typeof el.ident === 'string'
            ? el.ident
            : typeof el.name === 'string'
              ? canonicalize(el.name)
              : undefined;
        if (ident && members.has(ident) && typeof el.x === 'number' && typeof el.y === 'number') {
          out.set(ident, { x: el.x, y: el.y });
        }
      }
      if (out.size < members.size) {
        return undefined;
      }
      // The engine lays out for the compact SFD; text-only CLD nodes need more
      // room or the words collide. Spread only the crowded neighbourhoods (local
      // de-crowd) so the dense (12-loop) case stays contained instead of blowing
      // up under a single global scale, while the sparse case still separates.
      return spreadOverlaps(out, CLD_MIN_NODE_SPACING);
    } catch {
      return undefined;
    }
  }

  private async ensureCldView(): Promise<void> {
    if (this.creatingCldView) {
      return;
    }
    const project = this.project();
    const engine = this.engine();
    if (!project || !engine) {
      return;
    }
    const model = project.models.get(this.state.modelName);
    if (!model || this.state.loops.length === 0) {
      return;
    }
    // Already materialized: nothing to do. (Do NOT fit here -- ensureCldView is
    // called on every analysis refetch, and fitting bumps projectVersion, which
    // re-triggers the refetch -> an infinite loop. The fit-on-entry lives in
    // handleViewModeChange, which only fires on an actual mode switch.)
    if (model.views.length > 1) {
      return;
    }
    const sfdView = model.views[0];
    if (!sfdView) {
      return;
    }

    this.creatingCldView = true;
    try {
      const members = new Set<string>();
      for (const loop of this.state.loops) {
        for (const v of loop.variables) {
          members.add(v);
        }
      }
      // Owned-engine placement first (multi-loop legible; separates shared hubs),
      // hand-rolled ringLayout as the fallback when the engine can't place a member.
      const ring = (await this.enginePlacement(members)) ?? ringLayout(rankLoops(this.state.loops));
      const cldView = buildCldView(sfdView, [...members], ring);

      // Optimistically reflect the new view, then persist it at index 1.
      const views = [...model.views];
      views[1] = cldView;
      const updatedModel = { ...model, views };
      const activeProject = {
        ...project,
        models: mapSet(project.models, this.state.modelName, updatedModel),
      };
      this.setState({ activeProject, projectVersion: this.state.projectVersion + 0.001 });

      const ops: JsonModelOperation[] = [
        { type: 'upsertView', payload: { index: 1, view: stockFlowViewToJson(cldView) } },
      ];
      await engine.applyPatch({ models: [{ name: this.state.modelName, ops }] }, { allowErrors: true });
      await this.updateProject(await engine.serializeProtobuf());
      // Frame the freshly-laid-out CLD in the viewport (CO-5 #1).
      this.fitCurrentView();
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (ensure CLD view):', err.code, err.message, err.details);
    } finally {
      this.creatingCldView = false;
    }
  }

  // CO-5 per-loop color (Williams 2026-08-07): one map the canvas badges/rings and
  // the Loop Picker both consume, so a loop reads the same color in both places.
  // Colors are assigned in DOMINANCE-ranked order (cyan to the top R, slate to the
  // top B, then the palette), so recompute when the loop set, paper, OR the LTM
  // dominance shares change.
  private loopColorsRef?: object;
  private loopColorsPaper?: string;
  private loopColorsScoresRef?: object;
  private loopColorsCache?: Map<string, string>;

  private loopColors(): ReadonlyMap<string, string> {
    const paper = paperKind(this.state.canvasPaper);
    if (
      this.loopColorsRef !== this.state.loops ||
      this.loopColorsPaper !== paper ||
      this.loopColorsScoresRef !== this.state.loopScores
    ) {
      this.loopColorsRef = this.state.loops;
      this.loopColorsPaper = paper;
      this.loopColorsScoresRef = this.state.loopScores;
      this.loopColorsCache = assignLoopColors(rankLoops(this.state.loops, this.state.loopScores), paper);
    }
    return this.loopColorsCache ?? new Map<string, string>();
  }

  getCanvas() {
    const project = this.project();
    if (!project) {
      return;
    }

    const { embedded } = this.props;

    const model = this.getModel();
    if (!model) {
      return;
    }

    const view = this.getView();
    if (!view) {
      return;
    }

    // Stdlib models are read-only: disable all mutation handlers while
    // keeping selection, viewbox, and drill-in navigation active.
    const readOnly = embedded || isStdlibModel(this.state.modelName);
    const onRenameVariable = !readOnly ? this.handleRename : (_oldName: string, _newName: string): void => {};
    const onSetSelection = !embedded ? this.handleSelection : (_selected: ReadonlySet<UID>): void => {};
    const onMoveSelection = !readOnly ? this.handleSelectionMove : (_position: Point): void => {};
    const onMoveFlow = !readOnly ? this.handleFlowAttach : (_e: ViewElement, _t: number, _p: Point): void => {};
    const onMoveLabel = !readOnly
      ? this.handleMoveLabel
      : (_u: UID, _s: 'top' | 'left' | 'bottom' | 'right'): void => {};
    const onAttachLink = !readOnly ? this.handleLinkAttach : (_element: ViewElement, _to: string): void => {};
    const onCreateVariable = !readOnly ? this.handleCreateVariable : (_element: ViewElement): void => {};
    const onClearSelectedTool = !readOnly ? this.handleClearSelectedTool : () => {};
    const onDeleteSelection = !readOnly ? this.handleSelectionDelete : () => {};
    const onShowVariableDetails = !readOnly ? this.handleShowVariableDetails : () => {};
    const onViewBoxChange = !embedded ? this.handleViewBoxChange : () => {};
    const onDrillIntoModule = !embedded
      ? this.handleDrillIntoModule
      : (_moduleIdent: string, _targetModelName: string): void => {};

    // AC2.2 + CO-5: emphasise every highlighted loop (the click-pinned set plus
    // the hover preview) by glowing the union of their member variables on the
    // canvas via the variable-highlight overlay. Later loops win on a shared
    // member -- rare, and the badge color still reads.
    const highlightedLoopIds = this.highlightedLoopIdSet();
    const highlightedLoops = this.state.loops.filter((l) => highlightedLoopIds.has(l.id));
    let variableHighlights: ReturnType<typeof loopHighlights> | undefined;
    if (highlightedLoops.length > 0) {
      const paper = paperKind(this.state.canvasPaper);
      variableHighlights = new Map();
      for (const loop of highlightedLoops) {
        for (const [ident, highlight] of loopHighlights(loop.variables, loop.polarity, 'glow', paper)) {
          variableHighlights.set(ident, highlight);
        }
      }
    }

    return (
      <Canvas
        ref={this.canvasRef}
        embedded={!!embedded}
        showGrid={this.props.showCanvasGrid}
        canvasPaper={this.state.canvasPaper}
        project={project}
        model={model}
        view={view}
        version={this.state.projectVersion}
        renderMode={this.state.renderMode}
        linkPolarities={this.state.linkPolarities}
        loops={this.state.loops}
        onLoopHover={this.handleLoopHover}
        onLoopSelect={this.handleLoopSelect}
        highlightedLoopIds={highlightedLoopIds}
        loopColors={this.loopColors()}
        variableHighlights={variableHighlights}
        selectedTool={readOnly ? undefined : this.state.selectedTool}
        selection={this.state.selection}
        onRenameVariable={onRenameVariable}
        onSetSelection={onSetSelection}
        onMoveSelection={onMoveSelection}
        onMoveFlow={onMoveFlow}
        onMoveLabel={onMoveLabel}
        onAttachLink={onAttachLink}
        onCreateVariable={onCreateVariable}
        onClearSelectedTool={onClearSelectedTool}
        onDeleteSelection={onDeleteSelection}
        onShowVariableDetails={onShowVariableDetails}
        onViewBoxChange={onViewBoxChange}
        onDrillIntoModule={onDrillIntoModule}
      />
    );
  }

  handleCloseSnackbar = (msg: string) => {
    this.setState((prevState) => ({
      modelErrors: prevState.modelErrors.filter((err) => err.message !== msg),
    }));
  };

  // D2 criterion 14: flip between SFD and CLD render modes from the
  // header toggle.  Switching is intentionally a state-only change --
  // the model data and view positions stay put (criterion 20 forbids
  // mode flips from mutating the project), so we just write the new
  // mode and React re-renders Canvas with the new prop value.
  handleRenderModeChange = (renderMode: RenderMode): void => {
    if (renderMode === this.state.renderMode) {
      return;
    }
    this.setState({ renderMode });
  };

  getSnackbar() {
    const { embedded } = this.props;

    if (embedded) {
      return undefined;
    }

    return (
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={this.state.modelErrors.length > 0}
        autoHideDuration={6000}
      >
        <div>
          {this.state.modelErrors.map((err) => (
            <Toast
              variant="warning"
              onClose={this.handleCloseSnackbar}
              message={err.message}
              key={`${err.name}:${err.message}`}
            />
          ))}
        </div>
      </Snackbar>
    );
  }

  getSelectionIdents(): string[] {
    const names: string[] = [];
    const { selection } = this.state;
    const view = this.getView();
    if (!view) {
      return names;
    }

    for (const e of view.elements) {
      if (selection.has(e.uid) && isNamedViewElement(e)) {
        names.push(defined(e.ident));
      }
    }

    return names;
  }

  // FIXME: use a map
  getNamedSelectedElement(): ViewElement | undefined {
    if (this.state.selection.size !== 1) {
      return;
    }

    const uid = only(this.state.selection);

    const view = this.getView();
    if (!view) {
      return;
    }

    for (const e of view.elements) {
      if (e.uid === uid && isNamedViewElement(e)) {
        return e;
      }
    }

    return;
  }

  getNamedElement(ident: string): ViewElement | undefined {
    const view = this.getView();
    if (!view) {
      return;
    }

    for (const e of view.elements) {
      if (isNamedViewElement(e) && e.ident === ident) {
        return e;
      }
    }

    return;
  }

  handleShowDrawer = () => {
    this.setState({
      drawerOpen: true,
    });
  };

  handleDrillIntoModule = (moduleIdent: string, targetModelName: string): void => {
    const view = this.getView();
    if (!view) {
      return;
    }
    // Guard: don't push a nonexistent model onto the navigation stack.
    // Stdlib models are included in project.models because the editor
    // passes includeStdlib=true when calling serializeJson().
    const project = this.project();
    if (!project || !project.models.has(targetModelName)) {
      return;
    }
    const newStack = pushModule(
      this.state.modelStack,
      targetModelName,
      moduleIdent,
      this.state.selection,
      view.viewBox,
      view.zoom,
    );
    const newModelName = currentModelName(newStack);
    this.setState({
      modelStack: newStack,
      modelName: newModelName,
      selection: new Set<UID>(),
      showDetails: undefined,
      // Clear selected tool when entering a stdlib model (tool palette is hidden)
      selectedTool: isStdlibModel(newModelName) ? undefined : this.state.selectedTool,
    });
    // Refresh errors for the newly active model so warning dots and the
    // error panel reflect the child model's state immediately.
    setTimeout(() => void this.refreshCachedErrors());
  };

  handleNavigateBack = (): void => {
    const { modelStack } = this.state;
    if (modelStack.length === 0) {
      return;
    }
    const result = popModule(modelStack);
    this.setState({
      modelStack: result.newStack,
      modelName: result.restoredModelName,
      selection: result.restoredSelection,
      showDetails: undefined,
    });
    // Restore the parent model's viewport asynchronously via queueViewUpdate.
    // The parent model's view already exists in the project; we just need to
    // apply the saved viewBox and zoom back onto it.
    setTimeout(async () => {
      const view = this.getView();
      if (view) {
        await this.queueViewUpdate({ ...view, viewBox: result.restoredViewBox, zoom: result.restoredZoom });
      }
    });
    // Refresh errors for the restored model
    setTimeout(() => void this.refreshCachedErrors());
  };

  handleNavigateToLevel = (targetLevel: number): void => {
    const { modelStack } = this.state;
    if (targetLevel >= modelStack.length) {
      return;
    }
    const result = navigateToLevel(modelStack, targetLevel);
    this.setState({
      modelStack: result.newStack,
      modelName: result.restoredModelName,
      selection: result.restoredSelection,
      showDetails: undefined,
    });
    // Restore the target level's viewport asynchronously
    setTimeout(async () => {
      const view = this.getView();
      if (view) {
        await this.queueViewUpdate({ ...view, viewBox: result.restoredViewBox, zoom: result.restoredZoom });
      }
    });
    // Refresh errors for the target level's model
    setTimeout(() => void this.refreshCachedErrors());
  };

  handleSearchChange = async (_event: React.SyntheticEvent | null, newValue: string | null) => {
    if (!newValue) {
      this.handleSelection(new Set());
      return;
    }
    const element = this.getNamedElement(canonicalize(newValue));
    this.handleSelection(element ? new Set([element.uid]) : new Set());
    // Don't open the mutation-capable details panel for read-only
    // models (stdlib models, embedded mode). The Canvas-level guard
    // at line ~1480 handles double-click, but search bypasses it.
    const readOnly = this.props.embedded || isStdlibModel(this.state.modelName);
    this.setState({
      showDetails: readOnly ? undefined : 'variable',
    });
    if (element) {
      await this.centerVariable(element);
    }
  };

  handleStatusClick = () => {
    this.setState({
      showDetails: this.state.showDetails === 'errors' ? undefined : 'errors',
    });
  };

  getSearchBar() {
    const { embedded } = this.props;

    if (embedded) {
      return undefined;
    }

    let autocompleteOptions: Array<string> = [];
    const elements = this.getView()?.elements;
    if (elements) {
      autocompleteOptions = elements
        .filter((e) => isNamedViewElement(e))
        .map((e) => searchableName((e as NamedViewElement).name));
    }

    const namedElement = this.getNamedSelectedElement();
    let name;
    let placeholder: string | undefined = 'Find in Model';
    if (namedElement) {
      name = searchableName(defined((namedElement as NamedViewElement).name));
      placeholder = undefined;
    }

    const status = this.state.status;

    // WS8a density: Focus (sliverMinimal) collapses the sliver toward mode-only,
    // hiding the search field and SFD/CLD toggle. The mode switch and the density
    // selector always stay so the user is never stranded in Focus.
    const minimal = densityConfig(this.state.density).sliverMinimal;

    return (
      <div className={styles.searchBar}>
        <BreadcrumbBar
          modelStack={this.state.modelStack}
          modelName={this.state.modelName}
          onBack={this.handleNavigateBack}
          onNavigateToLevel={this.handleNavigateToLevel}
          onShowDrawer={this.handleShowDrawer}
          onHome={this.props.onHome}
        />
        {/* Mode toggle sits top-left, right next to the model name (Williams
            2026-08-07), ahead of the search field. */}
        <div className={styles.divider} />
        <BuildLoopsPlaySwitch mode={this.state.viewMode} onChange={this.handleViewModeChange} />
        <div className={styles.divider} />
        {!minimal && (
          <div className={styles.searchBox}>
            <Autocomplete
              key={name}
              value={name}
              onChange={this.handleSearchChange}
              clearOnEscape={true}
              defaultValue={name}
              options={autocompleteOptions}
              renderInput={(params: AutocompleteRenderInputParams) => {
                if (params.InputProps) {
                  params.InputProps.disableUnderline = true;
                }
                return <TextField {...params} variant="standard" placeholder={placeholder} fullWidth />;
              }}
            />
          </div>
        )}
        {minimal && <div className={styles.sliverSpacer} />}
        {!minimal && <div className={styles.divider} />}
        {!minimal && <RenderModeToggle mode={this.state.renderMode} onChange={this.handleRenderModeChange} />}
        <div className={styles.divider} />
        <DensitySelector density={this.state.density} onChange={this.handleDensityChange} />
        <div className={styles.divider} />
        <PaperSelector paper={this.state.canvasPaper} onChange={this.handlePaperChange} />
        <div className={styles.divider} />
        <Status status={status} onClick={this.handleStatusClick} />
        <div className={styles.divider} />
        {/* Save sits at the right end of the one toolbar shared by the SFD and
            CLD views, and is shown in every density (including Focus) so a
            first-time user always finds it. */}
        <SaveControl
          status={this.state.saveStatus}
          readOnly={!!this.props.readOnlyMode}
          persists={this.props.persists !== false}
          onSave={this.handleSaveNow}
        />
      </div>
    );
  }

  handleClearSelected = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    this.handleSelection(new Set());
  };

  // Returns the equation fields for a JSON patch operation.
  // For scalar equations, returns { equation: string }.
  // For arrayed equations, returns { arrayedEquation: JsonArrayedEquation }.
  getEquationFields(variable: Variable): { equation?: string; arrayedEquation?: JsonArrayedEquation } {
    const eq = variable.type === 'module' ? undefined : variable.equation;
    if (!eq || eq.type === 'scalar') {
      return { equation: eq?.equation ?? '' };
    } else if (eq.type === 'applyToAll') {
      return {
        arrayedEquation: {
          dimensions: [...eq.dimensionNames],
          equation: eq.equation,
        },
      };
    } else if (eq.type === 'arrayed') {
      return {
        arrayedEquation: {
          dimensions: [...eq.dimensionNames],
          elements: [...eq.elements.entries()].map(([subscript, eqStr]) => ({
            subscript,
            equation: eqStr,
          })),
        },
      };
    }
    return { equation: '' };
  }

  handleEquationChange = async (
    ident: string,
    newEquation: string | undefined,
    newUnits: string | undefined,
    newDocs: string | undefined,
  ) => {
    const engine = this.engine();
    if (!engine) {
      return;
    }

    const model = this.getModel();
    if (!model) {
      return;
    }

    const variable = model.variables.get(ident);
    if (!variable) {
      return;
    }

    // When newEquation is provided, use it as a scalar equation.
    // Otherwise, preserve the existing equation structure (including arrayed equations).
    const existingEqFields = this.getEquationFields(variable);

    let op: JsonModelOperation;
    if (variable.type === 'stock') {
      // Use stockToJson to preserve all fields (including compat flags
      // like nonNegative, canBeModuleInput, isPublic), then override
      // the fields being edited.
      const base = stockToJson(variable);
      op = {
        type: 'upsertStock',
        payload: {
          stock: {
            ...base,
            initialEquation: newEquation ?? existingEqFields.equation,
            arrayedEquation: newEquation !== undefined ? undefined : existingEqFields.arrayedEquation,
            units: newUnits ?? variable.units ?? undefined,
            documentation: newDocs ?? variable.documentation ?? undefined,
          },
        },
      };
    } else if (variable.type === 'flow') {
      const base = flowToJson(variable);
      op = {
        type: 'upsertFlow',
        payload: {
          flow: {
            ...base,
            equation: newEquation ?? existingEqFields.equation,
            arrayedEquation: newEquation !== undefined ? undefined : existingEqFields.arrayedEquation,
            units: newUnits ?? variable.units ?? undefined,
            documentation: newDocs ?? variable.documentation ?? undefined,
          },
        },
      };
    } else if (variable.type === 'module') {
      // Modules have no equations or graphical functions -- only units and docs
      op = {
        type: 'upsertModule',
        payload: {
          module: {
            name: variable.ident,
            modelName: variable.modelName,
            references: variable.references.map((r) => ({ src: r.src, dst: r.dst })),
            units: newUnits ?? variable.units ?? undefined,
            documentation: newDocs ?? variable.documentation ?? undefined,
          },
        },
      };
    } else {
      const auxVar = variable as Aux;
      const base = auxToJson(auxVar);
      op = {
        type: 'upsertAux',
        payload: {
          aux: {
            ...base,
            equation: newEquation ?? existingEqFields.equation,
            arrayedEquation: newEquation !== undefined ? undefined : existingEqFields.arrayedEquation,
            units: newUnits ?? auxVar.units ?? undefined,
            documentation: newDocs ?? auxVar.documentation ?? undefined,
          },
        },
      };
    }

    const patch: JsonProjectPatch = {
      models: [{ name: this.state.modelName, ops: [op] }],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (equation update):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error during equation update');
      return;
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  };

  handleTableChange = async (ident: string, newTable: GraphicalFunction | null) => {
    const engine = this.engine();
    if (!engine) {
      return;
    }

    const model = this.getModel();
    if (!model) {
      return;
    }

    const variable = model.variables.get(ident);
    if (!variable) {
      return;
    }

    const gf = newTable
      ? {
          yPoints: [...newTable.yPoints],
          kind: newTable.kind,
          xScale: newTable.xScale ? { min: newTable.xScale.min, max: newTable.xScale.max } : undefined,
          yScale: newTable.yScale ? { min: newTable.yScale.min, max: newTable.yScale.max } : undefined,
        }
      : undefined;

    // Preserve the existing equation structure when updating the graphical function
    const existingEqFields = this.getEquationFields(variable);

    // Use *ToJson to preserve all fields (including compat flags),
    // then override the graphical function.
    let op: JsonModelOperation;
    if (variable.type === 'flow') {
      const base = flowToJson(variable);
      op = {
        type: 'upsertFlow',
        payload: {
          flow: {
            ...base,
            equation: existingEqFields.equation,
            arrayedEquation: existingEqFields.arrayedEquation,
            graphicalFunction: gf,
          },
        },
      };
    } else {
      const auxVar = variable as Aux;
      const base = auxToJson(auxVar);
      op = {
        type: 'upsertAux',
        payload: {
          aux: {
            ...base,
            equation: existingEqFields.equation,
            arrayedEquation: existingEqFields.arrayedEquation,
            graphicalFunction: gf,
          },
        },
      };
    }

    const patch: JsonProjectPatch = {
      models: [{ name: this.state.modelName, ops: [op] }],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (table update):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error during table update');
      return;
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  };

  // Updates the model reference for a module variable.
  handleModuleModelReferenceChange = async (ident: string, newModelName: string) => {
    const engine = this.engine();
    if (!engine) return;
    const model = this.getModel();
    if (!model) return;
    const variable = model.variables.get(ident);
    if (!variable || variable.type !== 'module') return;

    const op: JsonModelOperation = {
      type: 'upsertModule',
      payload: {
        module: {
          name: variable.ident,
          modelName: newModelName,
          references: variable.references.map((r) => ({ src: r.src, dst: r.dst })),
          units: variable.units || undefined,
          documentation: variable.documentation || undefined,
        },
      },
    };

    const patch: JsonProjectPatch = {
      models: [{ name: this.state.modelName, ops: [op] }],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (model reference update):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error during model reference update');
      return;
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  };

  // Updates units and/or documentation for a module variable.
  handleModuleUnitsDocsChange = async (ident: string, newUnits: string | undefined, newDocs: string | undefined) => {
    const engine = this.engine();
    if (!engine) return;
    const model = this.getModel();
    if (!model) return;
    const variable = model.variables.get(ident);
    if (!variable || variable.type !== 'module') return;

    const op: JsonModelOperation = {
      type: 'upsertModule',
      payload: {
        module: {
          name: variable.ident,
          modelName: variable.modelName,
          references: variable.references.map((r) => ({ src: r.src, dst: r.dst })),
          units: newUnits ?? variable.units ?? undefined,
          documentation: newDocs ?? variable.documentation ?? undefined,
        },
      },
    };

    const patch: JsonProjectPatch = {
      models: [{ name: this.state.modelName, ops: [op] }],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (module units/docs update):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error during module update');
      return;
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  };

  // Updates the input references array for a module variable via upsertModule.
  // The engine does full variable replacement (not merge), so we send the
  // complete module with the new references array.
  handleModuleReferencesChange = async (ident: string, newReferences: ReadonlyArray<ModuleReference>) => {
    const engine = this.engine();
    if (!engine) return;
    const model = this.getModel();
    if (!model) return;
    const variable = model.variables.get(ident);
    if (!variable || variable.type !== 'module') return;

    const op: JsonModelOperation = {
      type: 'upsertModule',
      payload: {
        module: {
          name: variable.ident,
          modelName: variable.modelName,
          references: newReferences.map((r) => ({ src: r.src, dst: r.dst })),
          units: variable.units || undefined,
          documentation: variable.documentation || undefined,
        },
      },
    };

    const patch: JsonProjectPatch = {
      models: [{ name: this.state.modelName, ops: [op] }],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (references update):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error during references update');
      return;
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  };

  // Creates a new empty model and sets it as the module's reference.
  // The engine processes projectOps before model ops (see patch.rs),
  // so AddModel creates the model before upsertModule references it.
  handleCreateModelForModule = async (moduleIdent: string) => {
    const engine = this.engine();
    if (!engine) return;
    const project = this.project();
    if (!project) return;

    // Generate a unique model name to avoid collisions when the module
    // ident already matches an existing model name.
    let newModelName = moduleIdent;
    if (project.models.has(newModelName)) {
      newModelName = this.getUniqueDuplicateName(moduleIdent, project);
    }

    // Look up existing module to preserve metadata through the model reference change
    const model = this.getModel();
    const existingModule = model?.variables.get(moduleIdent);
    const modulePayload: { name: string; modelName: string; references?: { src: string; dst: string }[]; units?: string; documentation?: string } = {
      name: moduleIdent,
      modelName: newModelName,
    };
    if (existingModule && existingModule.type === 'module') {
      if (existingModule.references.length > 0) {
        modulePayload.references = existingModule.references.map((r) => ({ src: r.src, dst: r.dst }));
      }
      if (existingModule.units) modulePayload.units = existingModule.units;
      if (existingModule.documentation) modulePayload.documentation = existingModule.documentation;
    }

    const patch: JsonProjectPatch = {
      projectOps: [{ type: 'addModel', payload: { name: newModelName } }],
      models: [
        // Seed a default empty view so getCanvas() works after drilling in
        {
          name: newModelName,
          ops: [{ type: 'upsertView', payload: { index: 0, view: { elements: [] } } }],
        },
        {
          name: this.state.modelName,
          ops: [{ type: 'upsertModule', payload: { module: modulePayload } }],
        },
      ],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (create model for module):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error during model creation');
      return;
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  };

  // Duplicates the source model and sets the copy as the module's reference.
  // Copies all variables and the primary view from the source model.
  handleDuplicateModelForModule = async (moduleIdent: string, sourceModelName: string) => {
    const engine = this.engine();
    if (!engine) return;
    const project = this.project();
    if (!project) return;

    const sourceModel = project.models.get(sourceModelName);
    if (!sourceModel) return;

    const newModelName = this.getUniqueDuplicateName(sourceModelName, project);

    // Build ops to copy all variables from source model
    const variableOps: JsonModelOperation[] = [];
    for (const variable of sourceModel.variables.values()) {
      if (variable.type === 'stock') {
        variableOps.push({ type: 'upsertStock', payload: { stock: stockToJson(variable) } });
      } else if (variable.type === 'flow') {
        variableOps.push({ type: 'upsertFlow', payload: { flow: flowToJson(variable) } });
      } else if (variable.type === 'aux') {
        variableOps.push({ type: 'upsertAux', payload: { aux: auxToJson(variable) } });
      } else if (variable.type === 'module') {
        variableOps.push({ type: 'upsertModule', payload: { module: moduleToJson(variable) } });
      }
    }

    // Copy the primary view, or seed an empty one so getCanvas() works
    if (sourceModel.views.length > 0) {
      variableOps.push({
        type: 'upsertView',
        payload: { index: 0, view: stockFlowViewToJson(sourceModel.views[0]) },
      });
    } else {
      variableOps.push({
        type: 'upsertView',
        payload: { index: 0, view: { elements: [] } },
      });
    }

    // Preserve existing module metadata through the model reference change
    const currentModel = this.getModel();
    const existingModule = currentModel?.variables.get(moduleIdent);
    const dupModulePayload: { name: string; modelName: string; references?: { src: string; dst: string }[]; units?: string; documentation?: string } = {
      name: moduleIdent,
      modelName: newModelName,
    };
    if (existingModule && existingModule.type === 'module') {
      if (existingModule.references.length > 0) {
        dupModulePayload.references = existingModule.references.map((r) => ({ src: r.src, dst: r.dst }));
      }
      if (existingModule.units) dupModulePayload.units = existingModule.units;
      if (existingModule.documentation) dupModulePayload.documentation = existingModule.documentation;
    }

    // Combined patch: create model, copy contents, update module reference.
    // Engine processes projectOps before model ops (patch.rs).
    const patch: JsonProjectPatch = {
      projectOps: [{ type: 'addModel', payload: { name: newModelName } }],
      models: [
        { name: newModelName, ops: variableOps },
        {
          name: this.state.modelName,
          ops: [{
            type: 'upsertModule',
            payload: { module: dupModulePayload },
          }],
        },
      ],
    };

    try {
      await engine.applyPatch(patch, { allowErrors: true });
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      console.error('applyPatch error (duplicate model):', err.code, err.message, err.details);
      this.appendModelError(err.message ?? 'Unknown error during model duplication');
      return;
    }

    await this.updateProject(await engine.serializeProtobuf());
    this.scheduleSimRun();
  };

  private getUniqueDuplicateName(baseName: string, project: Project): string {
    let name = `${baseName}_copy`;
    let i = 2;
    while (project.models.has(name)) {
      name = `${baseName}_copy_${i}`;
      i++;
    }
    return name;
  }

  getErrorDetails() {
    const { cachedErrors } = this.state;

    return (
      <div className={styles.varDetails}>
        <ErrorDetails
          status={this.state.status}
          simError={cachedErrors.simError}
          modelErrors={cachedErrors.modelErrors}
          varErrors={cachedErrors.varErrors}
          varUnitErrors={cachedErrors.unitErrors}
        />
      </div>
    );
  }

  // Shows a thin info banner when inside a module whose model is shared
  // by multiple module instances, or when viewing a stdlib model.
  getSharedModelBanner(): React.ReactNode {
    const { modelStack, modelName } = this.state;
    if (modelStack.length === 0) return undefined;

    const project = this.project();
    if (!project) return undefined;

    // AC4.4: stdlib models show read-only message
    if (isStdlibModel(modelName)) {
      return (
        <div className={styles.sharedModelBanner}>
          Standard library model (read-only)
        </div>
      );
    }

    // AC4.1, AC4.2: count instances
    const count = countModelInstances(project, modelName);

    // AC4.3: single instance shows no banner
    if (count <= 1) return undefined;

    return (
      <div className={styles.sharedModelBanner}>
        This model is used by {count} modules &mdash; changes affect all instances
      </div>
    );
  }

  getDetails() {
    const { embedded } = this.props;

    if (embedded) {
      return;
    }

    if (this.state.flowStillBeingCreated) {
      return;
    }

    if (this.state.showDetails === 'errors') {
      return this.getErrorDetails();
    }

    const model = this.getModel();
    if (!model) {
      return;
    }

    // The selected variable, when a variable is selected and its details are
    // showing. `undefined` when nothing is selected, or during the brief
    // post-failed-rename window where the view element's ident is not yet in
    // model.variables (returning no editor for that one frame beats throwing
    // through render, which would unmount the Editor). In Play mode the rail
    // shows regardless, so Play is reachable without a selection.
    const namedElement = this.getNamedSelectedElement();
    const selectedIdent =
      namedElement && this.state.showDetails === 'variable' ? namedElement.ident : undefined;
    const selectedVariable = selectedIdent !== undefined ? model.variables.get(selectedIdent) : undefined;

    if (selectedVariable && selectedVariable.type === 'module') {
      return (
        <div className={styles.varDetails}>
          <ModuleDetails
            key={`md-${this.state.projectVersion}-${this.state.projectOffset}-${selectedIdent}`}
            variable={selectedVariable}
            viewElement={defined(namedElement)}
            project={defined(this.project())}
            currentModelName={this.state.modelName}
            onDelete={this.handleVariableDelete}
            onModelReferenceChange={this.handleModuleModelReferenceChange}
            onUnitsDocsChange={this.handleModuleUnitsDocsChange}
            onDrillIntoModule={this.handleDrillIntoModule}
            onCreateModel={this.handleCreateModelForModule}
            onDuplicateModel={this.handleDuplicateModelForModule}
            onReferencesChange={this.handleModuleReferencesChange}
          />
        </div>
      );
    }

    // Loops view (WS2, CO-4): the rail is the ranked Loop Picker rather than the
    // variable editor. A row hovers/pins the same highlight the CLD badges drive
    // (the AC2.2 seam), so clicking a loop here and clicking its badge on the
    // canvas are one and the same interaction.
    if (this.state.viewMode === 'loops') {
      // The Loop Picker has no ActivityRail tab-bar strip, so it uses its own
      // slot (loopsRail) that clears the toolbar rather than the shared
      // varDetails slot, which would tuck its header under the toolbar.
      return (
        <div className={styles.loopsRail}>
          <LoopPicker
            loops={rankLoops(this.state.loops, this.state.loopScores)}
            activeLoopIds={this.highlightedLoopIdSet()}
            loopColors={this.loopColors()}
            onHover={this.handleLoopHover}
            onSelect={this.handleLoopSelect}
          />
        </div>
      );
    }

    // The Activities rail is PERSISTENT (pinned) so it never vanishes with no way
    // back (WS8a punch-list #3): with nothing selected it shows the tab bar + a
    // "select a variable" placeholder instead of disappearing, and selecting a
    // variable fills the Variable tab -- surfacing the equation view (#6). Play is
    // reachable without a selection as before. (Focus-density edge-collapse is a
    // later refinement.)

    const activeTab = this.state.variableDetailsActiveTab;
    const activityTab = this.state.activeActivityTab;

    // WS4: the Agents tab is enabled only when the host injected a proposer AND the
    // user's tier can reach at least one WS4 persona (F1 gating). Otherwise the tab
    // stays visible-but-disabled so the rail's shape is constant across tiers.
    const agentPersonas = availablePersonas(WS4_PERSONAS, this.props.agentTier ?? Tier.Public);
    const agentsEnabled = (!!this.props.propose || !!this.props.describe) && agentPersonas.length > 0;
    const tabs: readonly ActivityRailTab[] = DEFAULT_ACTIVITY_TABS.map((t) =>
      t.id === 'agents' ? { ...t, enabled: agentsEnabled } : t,
    );

    // The variable editor is the Variable tab of the shared rail (WS2); Play is
    // the Play panel; Signal is disabled until WS3; Agents is WS4 (above).
    return (
      <div className={styles.varDetails}>
        <ActivityRail activeTab={activityTab} onTabChange={this.handleActivityTabChange} tabs={tabs}>
          {activityTab === 'variable' ? (
            selectedVariable && selectedIdent !== undefined ? (
              <VariableDetails
                key={`vd-${this.state.projectVersion}-${this.state.projectOffset}-${selectedIdent}`}
                variable={selectedVariable}
                viewElement={defined(namedElement)}
                getLatexEquation={this.getLatexEquation}
                activeTab={activeTab}
                onActiveTabChange={this.handleVariableDetailsActiveTabChange}
                onDelete={this.handleVariableDelete}
                onEquationChange={this.handleEquationChange}
                onTableChange={this.handleTableChange}
              />
            ) : (
              <div className={styles.activityPlaceholder}>Select a variable to edit its equation.</div>
            )
          ) : activityTab === 'play' ? (
            <PlayPanel
              parameters={modelParameters(
                Array.from(model.variables, ([varIdent, v]) => {
                  // Only a scalar equation can be a constant lever; arrayed /
                  // apply-to-all and modules have no single constant value.
                  const eq = v.type !== 'module' ? v.equation : undefined;
                  return {
                    ident: varIdent,
                    type: v.type,
                    equation: eq && eq.type === 'scalar' ? eq.equation : undefined,
                  };
                }),
              )}
              overrides={this.state.playOverrides}
              onOverrideChange={this.handlePlayOverrideChange}
              onResimulate={this.handlePlayResimulate}
              simulating={this.state.playSimulating}
            />
          ) : activityTab === 'agents' ? (
            <AgentPanel
              conversation={this.state.agentSession.conversation}
              personas={agentPersonas}
              activePersona={this.state.agentSession.activePersona}
              onSelectPersona={this.handleAgentSelectPersona}
              onSend={this.handleAgentSend}
              onCancel={this.handleAgentCancel}
              onDecide={this.handleAgentDecide}
              busy={this.state.agentSession.busy}
            />
          ) : (
            <div className={styles.activityPlaceholder}>Coming soon.</div>
          )}
        </ActivityRail>
      </div>
    );
  }

  handleVariableDetailsActiveTabChange = (variableDetailsActiveTab: number) => {
    this.setState({ variableDetailsActiveTab });
  };

  handleVariableDelete = (ident: string) => {
    const namedElement = this.getNamedSelectedElement();
    if (!namedElement) {
      return;
    }

    if (namedElement.ident !== ident) {
      return;
    }

    this.handleSelectionDelete();
  };

  handleClearSelectedTool = () => {
    this.setState({ selectedTool: undefined });
  };

  handleSelectStock = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      selectedTool: 'stock',
    });
  };

  handleSelectFlow = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      selectedTool: 'flow',
    });
  };

  handleSelectAux = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      selectedTool: 'aux',
    });
  };

  handleSelectLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      selectedTool: 'link',
    });
  };

  handleSelectModule = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      selectedTool: 'module',
    });
  };

  async refreshCachedErrors(): Promise<CachedErrorDetails | undefined> {
    const engine = this.engine();
    if (!engine) {
      return undefined;
    }

    const modelName = this.state.modelName;
    const errors = await engine.getErrors();
    const { varErrors, unitErrors } = convertErrorDetails(errors, modelName);

    let simError: SimError | undefined;
    const modelErrors: ModelError[] = [];
    for (const err of errors) {
      if (err.modelName && err.modelName !== modelName) {
        continue;
      }
      if (err.kind === SimlinErrorKind.Simulation) {
        simError = {
          code: err.code as unknown as ErrorCode,
          details: err.message ?? undefined,
        };
      } else if (!err.variableName) {
        modelErrors.push({
          code: err.code as unknown as ErrorCode,
          details: err.message ?? undefined,
        });
      }
    }
    const cachedErrors: CachedErrorDetails = { varErrors, unitErrors, simError, modelErrors };
    if (!this._isMounted) {
      // Engine.getErrors resolved after the component unmounted (e.g.
      // StrictMode's dev double-mount or the user navigating away).
      // Skip setState; the caller still gets the computed cache.
      return cachedErrors;
    }
    this.setState({ cachedErrors });
    return cachedErrors;
  }

  async updateVariableErrors(project: Project): Promise<Project> {
    const cached = await this.refreshCachedErrors();
    if (!cached) {
      return project;
    }

    const modelName = this.state.modelName;
    const { varErrors, unitErrors } = cached;

    if (varErrors.size > 0) {
      const model = getOrThrow(project.models, modelName);

      // if all the errors are 'just' that we have no equations,
      // don't scream "error" at the user -- they are starting from
      // scratch on a new model and don't expect it to be running yet.
      if (varErrors.size === model.variables.size && setsEqual(new Set(varErrors.keys()), new Set(model.variables.keys()))) {
        let foundOtherError = false;

        for (const [, errs] of varErrors) {
          if (errs.length !== 1 || first(errs).code !== ErrorCode.EmptyEquation) {
            foundOtherError = true;
            break;
          }
        }
        if (!foundOtherError) {
          return { ...project, hasNoEquations: true };
        }
      }

      const mutableVars = new Map(model.variables);
      for (const [ident, errs] of varErrors) {
        const variable = mutableVars.get(ident);
        if (variable) {
          mutableVars.set(ident, { ...variable, errors: errs });
        }
      }
      const updatedModel = { ...model, variables: mutableVars as ReadonlyMap<string, Variable> };
      project = { ...project, models: mapSet(project.models, modelName, updatedModel) };
    }

    if (unitErrors.size > 0) {
      const model = getOrThrow(project.models, modelName);
      const mutableVars = new Map(model.variables);
      for (const [ident, errs] of unitErrors) {
        const variable = mutableVars.get(ident);
        if (variable) {
          mutableVars.set(ident, { ...variable, unitErrors: errs });
        }
      }
      const updatedModel = { ...model, variables: mutableVars as ReadonlyMap<string, Variable> };
      project = { ...project, models: mapSet(project.models, modelName, updatedModel) };
    }

    return project;
  }

  async openInitialProject(): Promise<void> {
    let engine: EngineProject;
    try {
      if (this.props.inputFormat === 'json') {
        engine = await EngineProject.openJson(this.props.initialProjectJson);
      } else {
        engine = await EngineProject.openProtobuf(this.props.initialProjectBinary as Uint8Array);
      }
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      this.appendModelError(`opening the project in the engine failed: ${err.message ?? 'Unknown error'}`);
      return;
    }

    this.engineProject = engine;

    const serializedProject = await engine.serializeProtobuf();

    const json = JSON.parse(await engine.serializeJson(undefined, true)) as JsonProject;
    const project = await this.updateVariableErrors(projectFromJson(json));

    this.setState({
      projectHistory: [serializedProject],
      activeProject: project,
    });

    // D2 criterion 16: first fetch of link polarities, now that the
    // engine project is loaded.  Subsequent fetches are triggered by
    // `componentDidUpdate` whenever the project version advances.
    void this.refetchLinkPolarities();
  }

  async openEngineProject(serializedProject: Readonly<Uint8Array>): Promise<EngineProject | undefined> {
    await this.engineProject?.dispose();
    this.engineProject = undefined;

    let engine: EngineProject;
    try {
      engine = await EngineProject.openProtobuf(serializedProject as Uint8Array);
    } catch (e: unknown) {
      const err = getErrorDetails(e);
      this.appendModelError(`opening the project in the engine failed: ${err.message ?? 'Unknown error'}`);
      return;
    }
    this.engineProject = engine;

    const json = JSON.parse(await engine.serializeJson(undefined, true)) as JsonProject;
    let project = projectFromJson(json);

    if (this.newEngineShouldPullView) {
      const queuedView = defined(this.newEngineQueuedView);
      this.newEngineShouldPullView = false;
      this.newEngineQueuedView = undefined;
      const model = defined(project.models.get(this.state.modelName));
      const views = [...model.views];
      views[0] = queuedView;
      const updatedModel = { ...model, views };
      project = { ...project, models: mapSet(project.models, this.state.modelName, updatedModel) };
      this.queueViewUpdate(queuedView);
    }

    this.setState({
      activeProject: await this.updateVariableErrors(project),
    });

    return engine;
  }

  async recalculateStatus() {
    const project = this.project();
    const engine = this.engine();

    let status: 'ok' | 'error' | 'disabled';
    if (!engine || !project || project.hasNoEquations) {
      status = 'disabled';
    } else if (!(await engine.isSimulatable())) {
      status = 'error';
    } else {
      status = 'ok';
    }

    this.setState({
      status,
    });
  }

  handleUndoRedo = (kind: 'undo' | 'redo') => {
    const delta = kind === 'undo' ? 1 : -1;
    let projectOffset = this.state.projectOffset + delta;
    // ensure our offset is always valid
    projectOffset = Math.min(projectOffset, this.state.projectHistory.length - 1);
    projectOffset = Math.max(projectOffset, 0);
    const serializedProject = defined(this.state.projectHistory[projectOffset]);
    const projectVersion = this.state.projectVersion + 0.01;
    this.setState({ projectOffset, projectVersion });

    setTimeout(async () => {
      await this.openEngineProject(serializedProject);
      // After undo/redo, the restored project may not contain the model
      // we were viewing (e.g. undo after creating and drilling into a new
      // submodel). Reset navigation state if the current model is gone.
      const project = this.project();
      if (project && this.state.modelStack.length > 0 && !project.models.has(this.state.modelName)) {
        this.setState({
          modelStack: [],
          modelName: 'main',
          selection: new Set<UID>(),
          showDetails: undefined,
          selectedTool: undefined,
        });
      }
      this.scheduleSimRun();
      this.scheduleSave();
    });
  };

  handleZoomChange = async (newZoom: number) => {
    const view = defined(this.getView());
    const oldViewBox = view.viewBox;

    const widthAdjust = this.state.showDetails ? SearchbarWidthLg : 0;

    const oldViewWidth = (oldViewBox.width - widthAdjust) / view.zoom;
    const oldViewHeight = oldViewBox.height / view.zoom;

    const newViewWidth = (oldViewBox.width - widthAdjust) / newZoom;
    const newViewHeight = oldViewBox.height / newZoom;

    const diffX = (newViewWidth - oldViewWidth) / 2;
    const diffY = (newViewHeight - oldViewHeight) / 2;

    const newViewBox: Rect = {
      ...oldViewBox,
      x: oldViewBox.x + diffX,
      y: oldViewBox.y + diffY,
    };
    await this.handleViewBoxChange(newViewBox, newZoom);
  };

  takeSnapshot() {
    const project = this.project();
    if (!project || !this.state.modelName) {
      return;
    }
    const { modelName } = this.state;

    const [svg, viewbox] = renderSvgToString(project, modelName);
    const osCanvas = document.createElement('canvas');
    osCanvas.width = viewbox.width * 4;
    osCanvas.height = viewbox.height * 4;
    const ctx = exists(osCanvas.getContext('2d'));
    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const image = new Image();
    image.onload = () => {
      ctx.drawImage(image, 0, 0, viewbox.width * 4, viewbox.height * 4);

      osCanvas.toBlob((snapshotBlob) => {
        if (snapshotBlob) {
          this.setState({ snapshotBlob });
        } else {
          this.setState({
            modelErrors: [...this.state.modelErrors, new Error('snapshot creation failed (1).')],
          });
        }
      });
    };
    image.onerror = () => {
      this.setState({
        modelErrors: [...this.state.modelErrors, new Error('snapshot creation failed (2).')],
      });
    };

    image.src = svgUrl;
  }

  handleSnapshot = (kind: 'show' | 'close') => {
    if (kind === 'show') {
      setTimeout(() => {
        this.takeSnapshot();
      });
    }
  };

  getMetaActionsBar() {
    const { embedded } = this.props;
    if (embedded) {
      return undefined;
    }

    const zoom = this.getView()?.zoom || 1;

    return (
      <div className={styles.undoRedoBar}>
        <UndoRedoBar
          undoEnabled={this.isUndoEnabled()}
          redoEnabled={this.isRedoEnabled()}
          onUndoRedo={this.handleUndoRedo}
        />
        {/*<Snapshotter onSnapshot={this.handleSnapshot} />*/}
        <ZoomBar zoom={zoom} onChangeZoom={this.handleZoomChange} />
        <IconButton aria-label="Fit to view" onClick={this.handleFitToView}>
          <FitToViewIcon />
        </IconButton>
      </div>
    );
  }

  handleFitToView = (): void => {
    this.canvasRef.current?.fitToView();
  };

  getEditorControls() {
    const { embedded } = this.props;
    const { dialOpen, dialVisible, selectedTool } = this.state;

    if (embedded || isStdlibModel(this.state.modelName)) {
      return undefined;
    }

    return (
      <SpeedDial
        ariaLabel="hide or show editor tools"
        className={styles.speedDial}
        hidden={!dialVisible}
        icon={<SpeedDialIcon icon={<EditIcon />} openIcon={<ClearIcon />} />}
        onClick={this.handleDialClick}
        onClose={this.handleDialClose}
        open={dialOpen}
      >
        <SpeedDialAction
          icon={<StockIcon />}
          title="Stock"
          onClick={this.handleSelectStock}
          selected={selectedTool === 'stock'}
        />
        <SpeedDialAction
          icon={<FlowIcon />}
          title="Flow"
          onClick={this.handleSelectFlow}
          selected={selectedTool === 'flow'}
        />
        <SpeedDialAction
          icon={<AuxIcon />}
          title="Variable"
          onClick={this.handleSelectAux}
          selected={selectedTool === 'aux'}
        />
        <SpeedDialAction
          icon={<LinkIcon />}
          title="Link"
          onClick={this.handleSelectLink}
          selected={selectedTool === 'link'}
        />
        <SpeedDialAction
          icon={<ModuleIcon />}
          title="Module"
          onClick={this.handleSelectModule}
          selected={selectedTool === 'module'}
        />
      </SpeedDial>
    );
  }

  getSnapshot() {
    const { embedded } = this.props;
    const { snapshotBlob } = this.state;

    if (embedded || !snapshotBlob) {
      return undefined;
    }

    return (
      <div className={styles.snapshotCard}>
        <div className={styles.snapshotCardContent}>
          <img src={URL.createObjectURL(snapshotBlob)} className={styles.snapshotImg} alt="diagram snapshot" />
        </div>
        <div className={styles.snapshotCardActions}>
          <Button size="small" color="primary" onClick={this.handleClearSnapshot}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  handleClearSnapshot = () => {
    this.setState({ snapshotBlob: undefined });
  };

  /** CO-5 #9: default Results plot set -- the model's stocks that have data. */
  private defaultResultsIdents(): Set<string> {
    const idents = new Set<string>();
    const model = this.getModel();
    if (!model) {
      return idents;
    }
    for (const [ident, v] of model.variables) {
      if (v.type === 'stock' && this.state.data.get(ident) !== undefined) {
        idents.add(ident);
      }
    }
    return idents;
  }

  handleResultsAddVar = (ident: string): void => {
    const current = this.state.resultsSeriesIdents ?? this.defaultResultsIdents();
    const next = new Set(current);
    next.add(ident);
    this.setState({ resultsSeriesIdents: next });
  };

  handleResultsRemoveVar = (ident: string): void => {
    const current = this.state.resultsSeriesIdents ?? this.defaultResultsIdents();
    const next = new Set(current);
    next.delete(ident);
    this.setState({ resultsSeriesIdents: next });
  };

  getResultsBand() {
    if (this.props.embedded) {
      return;
    }
    const model = this.getModel();
    if (!model) {
      return;
    }
    // CO-5 #9: plot the user's chosen variables; default to the model's stocks
    // until they add/remove. The band appears once a run has produced data.
    const effective = this.state.resultsSeriesIdents ?? this.defaultResultsIdents();
    const series: Series[] = [];
    for (const ident of effective) {
      const s = this.state.data.get(ident);
      if (s !== undefined) {
        series.push(s);
      }
    }
    // Everything simulated (minus the time axis) can be added to the plot.
    const availableVars: string[] = [];
    for (const ident of this.state.data.keys()) {
      if (ident !== 'time') {
        availableVars.push(ident);
      }
    }
    if (series.length === 0 && availableVars.length === 0) {
      return;
    }
    // WS8a: the band fills its Results zone. Maximize is now a reflow (the Results
    // zone grows and the Model shrinks to its minimum -- see the resultsHeight
    // computed in render()), not an overlay covering the canvas.
    return (
      <div className={styles.resultsBand}>
        <ResultsBand
          series={series}
          availableVars={availableVars}
          onAddVar={this.handleResultsAddVar}
          onRemoveVar={this.handleResultsRemoveVar}
          layout={this.state.resultsBandLayout}
          onLayoutChange={this.handleResultsBandLayoutChange}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { embedded } = this.props;

    const classNames = clsx(styles.editor, embedded ? '' : styles.editorBg);

    // The canvas-paper tint is applied as a data attribute so the canvas-surface
    // CSS custom properties + the locked notation colors can be overridden for this
    // subtree (see theme.css / the CLD module CSS). Tints white/cream/yellow are the
    // light-mode axis; 'black' is dark mode (the notation inverts against it, D5).
    const paper = this.state.canvasPaper;

    // Embedded (read-only preview / host embed) keeps the flat canvas layout.
    if (embedded) {
      return (
        <div className={classNames} data-canvas-paper={paper}>
          {this.getDrawer()}
          {this.getCanvas()}
          {this.getSnackbar()}
          {this.getEditorControls()}
          {this.getMetaActionsBar()}
          {this.getSnapshot()}
        </div>
      );
    }

    // WS8a zone frame: the toolbar, canvas, Activities rail, and Results band
    // live inside bounded, non-overlapping zones instead of floating at absolute
    // offsets (the structural NC-1 fix). The rail/band zones only take space when
    // there's content; the Model (canvas) absorbs the rest and is never covered.
    // Other chrome (drawer, snackbar, snapshot, banners) overlays the frame.
    const activities = this.getDetails();
    const results = this.getResultsBand();
    // Maximize = reflow: a very tall request that computeZoneLayout clamps so the
    // Model keeps its minimum height and the band takes the rest of the center.
    const resultsMaximized = this.state.resultsBandLayout.mode === 'maximized';
    const resultsHeight = results ? (resultsMaximized ? Number.MAX_SAFE_INTEGER : this.state.zoneResultsHeight) : 0;
    return (
      <div className={classNames} data-canvas-paper={paper}>
        <ZoneFrame
          sliverHeight={densityConfig(this.state.density).sliverHeight}
          toolsWidth={0}
          activitiesWidth={activities ? this.state.zoneActivitiesWidth : 0}
          resultsHeight={resultsHeight}
          onResize={this.handleZoneResize}
          sliver={this.getSearchBar()}
          model={
            <>
              {this.getCanvas()}
              {/* The build-tools FAB + undo/zoom live INSIDE the Model zone so they
                  sit at the canvas's bottom-left, above the Results band -- as
                  editor-root overlays they sat on top of the Results chart
                  (WS8a punch-list #2). */}
              {this.getEditorControls()}
              {this.getMetaActionsBar()}
            </>
          }
          activities={activities}
          results={results}
        />
        {this.getDrawer()}
        {this.getSharedModelBanner()}
        {this.getSnackbar()}
        {this.getSnapshot()}
      </div>
    );
  }
}
